import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MaleIcon from '@mui/icons-material/Male';
import PetsIcon from '@mui/icons-material/Pets';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FavoriteIcon from '@mui/icons-material/Favorite';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import { hasActionPermission } from '../../utils/accessControl';
import {
  timelineNote,
  timelineStatusLabel,
  timelineTitle,
  timelineTypeLabel as timelineTypeText,
} from './reproductionTimelineLabels';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import {
  formatDateDisplay,
  formatDateInput,
  formatDateTimeDisplay,
  formatDateTimeInput,
  todayDateInput,
  nowDateTimeInput,
} from '../../utils/dateUtils';

const HIDDEN_OPERATIONAL_TABS = new Set(['timesheets', 'reports']);

// 3.7.1 — صفحات الإنتاج الخمس كانت تُفتح من /settings وحدها: تسجيل الولادة وإسناد
// الحاضنات ونقل الأجنّة عملُ الطبيب اليوميّ لا إعداد. وجدول القائمة الجانبيّة لا يعرف
// التداخل (يُرسم مسطّحًا)، فإضافة خمسة صفوف إليه تزيد الزحام لا الترتيب — لذا صارت
// عيادة الإنتاج **مركزًا**: مدخل واحد في القائمة، ومنه تُفتح الخمس بنقرة.
//
// ما يبقى في الإعدادات: «إجراءات الإنتاج» وحدها — فهي كتالوج وأسعار، أي إعداد فعليّ.
const REPRO_HUB = [
  { key: 'embryoTransfer', path: '/EmbryoTransfer', page: 'embryotransfer' },
  { key: 'foalingRecords', path: '/FoalingRecords', page: 'foalingrecords' },
  { key: 'recipientMares', path: '/RecipientMares', page: 'recipientmares' },
  { key: 'stallions', path: '/StallionForm', page: 'stallionform' },
  { key: 'semenInventory', path: '/semen-inventory', page: 'semen-inventory' },
];

const REPRO_HUB_ICONS = {
  embryoTransfer: <SwapHorizIcon />,
  foalingRecords: <ChildFriendlyIcon />,
  recipientMares: <PetsIcon />,
  stallions: <MaleIcon />,
  semenInventory: <Inventory2Icon />,
};

/**
 * 3.7.2 — زرّ سياقيّ واحد بحسب موضع الحالة في مسارها، يفتح الصفحة المتخصّصة على هذه
 * الحالة بدل أن يبحث المستخدم عنها. الخطوة التالية تُشتقّ من نوع الحالة وحالتها معًا:
 * مانحة ملقّحة ⇐ الغسل، مانحة مغسولة ⇐ النقل، حاملٌ ⇐ تسجيل الولادة.
 *
 * ويُعاد `null` حين لا تكون هناك خطوة واضحة: زرٌّ يظهر دائمًا يفقد معناه، ومَن يراه في
 * غير موضعه يضغطه في غير موضعه.
 */
function nextCaseAction(row) {
  if (!row) return null;
  const type = row.case_type;
  const status = row.case_status;
  if (type === 'Embryo Transfer Donor') {
    if (status === 'Flushed') return { key: 'goTransfer', path: '/EmbryoTransfer', page: 'embryotransfer' };
    if (['Inseminated', 'Ovulated'].includes(status)) return { key: 'goFlush', path: '/EmbryoTransfer', page: 'embryotransfer' };
  }
  if (type === 'Embryo Transfer Recipient' && ['Open', 'Waiting Ovulation', 'Ovulated'].includes(status)) {
    return { key: 'goSync', path: '/EmbryoTransfer', page: 'embryotransfer' };
  }
  if (status === 'Pregnant') return { key: 'goFoaling', path: '/FoalingRecords', page: 'foalingrecords' };
  return null;
}

function ReproHub({ text, navigate }) {
  // تُخفى البطاقة التي لا يملك المستخدم صلاحيّتها: فتحُ باب يؤدّي إلى «لا صلاحية» أسوأ
  // من غيابه.
  const items = REPRO_HUB.filter((item) => hasActionPermission(item.page));
  if (!items.length) return null;
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 2.5 }}>
      <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 900, display: 'block', mb: 1 }}>
        {text.hubTitle}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {items.map((item) => (
          <Button
            key={item.key}
            onClick={() => navigate(item.path)}
            startIcon={REPRO_HUB_ICONS[item.key]}
            sx={{ bgcolor: COLORS.cardSoft, color: COLORS.text, fontWeight: 900, px: 2, '&:hover': { bgcolor: COLORS.goldSoft } }}
          >
            {text[item.key]}
          </Button>
        ))}
      </Stack>
    </Paper>
  );
}

// نافذة عمل المهامّ: ما يستحقّ خلال ٤٨ ساعة وما فات. تُطلب صراحةً من الخادم فلا
// تعتمد هذه الشاشة على افتراضٍ فيه.
const TASK_WINDOW_HOURS = 48;
const INSEMINATION_ACTION_KEY = 'Insemination';
// 3.7.0 — إجراءات البروتوكول التي لم تكن لها واجهة إطلاقًا في المسارين: تحفيز الإباضة،
// وتأكيد الإباضة، والتطعيم، والمتابعة. مساراتها في الخادم منذ البداية بلا مستهلك.
//
// أثر غيابها لم يكن «حقلًا فارغًا» بل انقطاع السلسلة كلّها: تاريخ الإباضة المؤكَّد هو ما
// يُشتقّ منه موعد فحص الحمل (+١٤) والولادة المتوقّعة (+٣٤٠) ونافذة غسل الأجنّة (٧–٨).
// وبلا تسجيله لا يُنشأ موعد محسوب واحد، ولا تُولد مهمّة «تأكيد الإباضة» أصلًا — وهو سبب
// خلوّ تبويب المهامّ ولوحة المتابعة في نظام فيه ٣٣ حالة.
const OVULATION_TRIGGER_KEY = 'Ovulation Trigger';
const OVULATION_CONFIRM_KEY = 'Ovulation Confirmation';
const VACCINATION_KEY = 'Vaccination';
const FOLLOWUP_KEY = 'Follow-up';

// إجراءات لا تُسجَّل كفحص: لكلٍّ منها جدوله ومساره في الخادم.
const PROTOCOL_ACTION_KEYS = [OVULATION_TRIGGER_KEY, OVULATION_CONFIRM_KEY, VACCINATION_KEY, FOLLOWUP_KEY];

const PROTOCOL_ACTION_OPTIONS = [
  { exam_type_key: OVULATION_TRIGGER_KEY, name_en: 'Ovulation trigger', name_ar: 'تحفيز الإباضة', is_active: 1 },
  { exam_type_key: OVULATION_CONFIRM_KEY, name_en: 'Ovulation confirmation', name_ar: 'تأكيد الإباضة', is_active: 1 },
  { exam_type_key: VACCINATION_KEY, name_en: 'Vaccination', name_ar: 'تطعيم', is_active: 1 },
  { exam_type_key: FOLLOWUP_KEY, name_en: 'Follow-up', name_ar: 'متابعة', is_active: 1 },
];

const TRIGGER_MEDICATIONS = ['hCG', 'Deslorelin', 'GnRH', 'Other'];
const VACCINATION_STATUSES = ['Pending', 'Done', 'Skipped', 'Cancelled'];
const VACCINE_NAMES = ['EHV', 'Influenza', 'Tetanus', 'Rhino', 'Other'];
const FOLLOWUP_TYPES = [
  'Phone Call', 'Clinic Visit', 'Ultrasound Follow-up', 'Medication Follow-up',
  'Pregnancy Follow-up', 'Embryo Transfer Follow-up', 'Foaling Follow-up',
  'Client Update', 'Internal Note', 'Other',
];

function createEmptyTrigger() {
  return { trigger_date_time: nowDateTimeInput(), trigger_medication: 'hCG', trigger_dose: '', follicle_size_at_trigger_mm: '', uterine_edema_grade: '', notes: '' };
}

function createEmptyConfirm() {
  return { ovulation_id: '', confirmed_ovulation_date_time: nowDateTimeInput() };
}

function createEmptyVaccination() {
  return { vaccine_name: 'EHV', due_month_of_pregnancy: '', due_date: todayDateInput(), status: 'Done', administered_date: todayDateInput(), notes: '' };
}

function createEmptyFollowupAction() {
  return { followup_date_time: nowDateTimeInput(), followup_type: 'Clinic Visit', followup_status: 'Completed', notes: '', next_action_required: false, next_action_description: '', next_action_due_date: '' };
}

const INSEMINATION_ACTION_OPTION = {
  exam_type_key: INSEMINATION_ACTION_KEY,
  name_en: 'Insemination',
  name_ar: 'تلقيح',
  is_active: 1,
};

const COPY = {
  ar: {
    title: 'عيادة الإنتاج',
    subtitle: 'تشغيل كامل لحالات الإنتاج: فحوصات، إباضة، تلقيح، حمل، تقويم، مهام، إشعارات وبروتوكولات قابلة للتعديل.',
    disclaimer: 'هذه القيم إعدادات إرشادية قابلة للتعديل ويجب مراجعتها واعتمادها من الطبيب البيطري المسؤول. النظام يساعد في الجدولة والتوثيق ولا يغني عن القرار الطبي.',
    dashboard: 'لوحة التشغيل',
    seasonFollowup: 'متابعة الموسم',
    seasonFollowupHint: 'عرض تشغيلي مطابق لمتطلبات ملف Embryos and Pregnancies: الفرس، الفحل، الإباضة، نبض الحمل، P4، التطعيمات، الديدان، موعد الولادة والملاحظات.',
    embryosSeason: 'موسم الأجنة',
    pregnanciesSeason: 'موسم الحمل',
    caseSerial: 'رقم الحالة',
    mareRoom: 'R / الموقع',
    stallion: 'الفحل',
    addStallion: 'إضافة الفحل',
    stallionHint: 'اكتب اسم الفحل هنا حتى لو كان السائل المنوي من خارج المربط. عند اختيار مصدر السائل خارجي سيتم حفظ اسم المورد أو المركز ضمن ملاحظات سجل التلقيح.',
    selectStallion: 'اختر الفحل',
    externalStallionAllowed: 'يمكن اختيار فحل مسجل أو كتابة اسم فحل/مصدر خارجي غير موجود في القائمة.',
    semenType: 'نوع السائل',
    inseminationDate: 'تاريخ التلقيح',
    semenSource: 'مصدر السائل',
    semenProvider: 'مورد / مركز السائل',
    semenBatch: 'دفعة المخزون',
    dosesUsed: 'عدد الجرعات',
    dosesWord: 'جرعة',
    noBatchesForStallion: 'لا دفعات متاحة لهذا الفحل في المخزون',
    batchHint: 'يُخصم من مخزون السائل المنوي عند الحفظ',
    doseDescription: 'وصف الجرعة',
    performedBy: 'نفذ بواسطة',
    semenSourceLabels: {
      'In-house': 'من المربط',
      External: 'خارجي',
    },
    ovulationShort: 'OV',
    heartbeatShort: 'HB+',
    stopP4: 'STOP P4',
    vaccine5m: '5M',
    vaccine7m: '7M',
    vaccine9m: '9M',
    deworm: 'DEWORM',
    dueDate: 'DUE DATE',
    obs: 'OBS',
    amount: 'المبلغ',
    total: 'الإجمالي',
    paid: 'المدفوع',
    due: 'المتبقي',
    cases: 'الحالات',
    timeline: 'الخط الزمني',
    calendar: 'التقويم',
    tasks: 'المهام',
    notifications: 'الإشعارات',
    assignedTo: 'مُسنَدة إلى',
    unassigned: 'غير مُسنَدة',
    markDone: 'أنجزت',
    taskActions: 'إجراءات المهمة',
    hubTitle: 'صفحات عيادة الإنتاج',
    rangeWeek: 'أسبوع',
    goFlush: 'غسل الأجنّة ←',
    goTransfer: 'نقل الجنين إلى حاضنة ←',
    goSync: 'ربط بمانحة ←',
    goFoaling: 'تسجيل الولادة ←',
    rangeMonth: 'شهر',
    timelineScope: 'نطاق الخط الزمني',
    thisCaseOnly: 'هذه الحالة فقط',
    embryoTransfer: 'نقل الأجنّة',
    foalingRecords: 'الولادات والمواليد',
    recipientMares: 'الأفراس الحاضنة',
    stallions: 'بيانات الفحل',
    semenInventory: 'مخزون السائل المنوي',
    triggerHint: 'يحسب النظام نافذة تأكيد الإباضة (36–48 ساعة) وينشئ مهمتها وحدث تقويمها تلقائيًا.',
    triggerDateTime: 'وقت التحفيز',
    triggerMedication: 'دواء التحفيز',
    triggerDose: 'الجرعة',
    follicleSize: 'حجم الحويصلة (مم)',
    edemaGrade: 'درجة وذمة الرحم',
    confirmHint: 'من هذا التاريخ يحسب النظام موعد فحص الحمل (+14) والولادة المتوقعة (+340) ونافذة غسل الأجنة.',
    triggerRecord: 'سجل التحفيز',
    confirmedOvulationDateTime: 'وقت الإباضة المؤكد',
    alreadyConfirmed: 'مؤكدة',
    noTriggerYet: 'لا يوجد تحفيز مسجل لهذه الحالة. سجّل «تحفيز الإباضة» أولًا.',
    selectTriggerFirst: 'اختر سجل التحفيز أولًا.',
    vaccineName: 'اللقاح',
    pregnancyMonth: 'شهر الحمل (إلزامي)',
    pregnancyMonthRequired: 'شهر الحمل مطلوب.',
    vaccineDueDate: 'تاريخ الاستحقاق',
    administeredDate: 'تاريخ الإعطاء',
    followupDateTime: 'وقت المتابعة',
    followupType: 'نوع المتابعة',
    nextActionRequired: 'يتطلب إجراءً لاحقًا',
    nextActionDescription: 'وصف الإجراء اللاحق',
    nextActionDue: 'موعد الإجراء اللاحق',
    cancelTask: 'إلغاء المهمة',
    confirmCancelTask: 'تأكيد الإلغاء',
    cancelReason: 'سبب الإلغاء (إلزامي)',
    taskResult: 'النتيجة (اختيارية)',
    recordAction: 'تسجيل الإجراء في الحالة',
    taskCancelled: 'أُلغيت المهمة.',
    tasksWindowHint: 'المعروض: ما يستحق خلال 48 ساعة وما فات موعده.',
    noTasksDue: 'لا مهام مستحقة الآن.',
    overdue: 'فات موعدها',
    completionNote: 'ملاحظة الإغلاق (اختيارية)',
    taskCompleted: 'تم إنجاز المهمة.',
    saveFailed: 'تعذّر الحفظ.',
    timesheets: 'ساعات العمل',
    notificationHint: 'الإشعارات هنا للتنبيهات الاستثنائية فقط مثل المتأخرات والتحذيرات الطبية. أما المواعيد اليومية فتظهر في التقويم.',
    selectHorse: 'اختر الخيل',
    allHorses: 'كل الخيل',
    timesheetHint: 'ساعات العمل تقيس وقت الطبيب والفريق على كل خيل وحالة، وتفيد في معرفة الضغط التشغيلي، تكلفة الوقت، ومراجعة الأعمال قبل اعتمادها.',
    timesheetTotalTime: 'إجمالي الوقت',
    timesheetBillableTime: 'وقت قابل للفوترة',
    timesheetCost: 'تكلفة الوقت',
    timesheetPending: 'بانتظار الاعتماد',
    horseTimelineHint: 'اختر خيلاً لعرض خط زمني موحد يجمع كل حالات الإنتاج الخاصة به.',
    openCase: 'عرض الحالة',
    addAction: 'إضافة إجراء',
    scheduleAction: 'إجراء من التقويم',
    scheduledItem: 'موعد مجدول',
    taskItem: 'مهمة',
    calendarLegend: 'دليل ألوان التقويم',
    calendarAuto: 'مجدول آلياً',
    calendarDoctor: 'تعديل الطبيب',
    calendarManual: 'موعد يدوي',
    calendarOverdue: 'متأخر',
    calendarWarning: 'تحذير',
    calendarPregnancy: 'متابعة حمل',
    protocol: 'البروتوكول',
    reports: 'التقارير',
    newCase: 'حالة جديدة',
    editCase: 'تعديل الحالة',
    deleteCase: 'تعطيل الحالة',
    deleteCaseTitle: 'تعطيل حالة الإنتاج',
    deleteCaseConfirm: 'سيتم إخفاء الحالة من لوحة التشغيل والتقويم مع الاحتفاظ بسجلها للمراجعة. اكتب سبب التعطيل للمتابعة.',
    deleteReason: 'سبب التعطيل',
    deleted: 'تم تعطيل الحالة بأمان.',
    addExam: 'إضافة إجراء',
    refresh: 'تحديث',
    procedures: 'إجراءات الإنتاج',
    legacyRecord: 'تسجيل تلقيح قديم',
    openCases: 'الحالات المفتوحة',
    waitingOvulation: 'بانتظار الإباضة',
    pregnantCases: 'حالات حمل',
    foalingWatch: 'مراقبة ولادة',
    schedule: 'جدول قريب',
    dueTasks: 'مهام قريبة',
    weeklyClinicPlan: 'جدول الأسبوع التشغيلي',
    monthlyClinicCalendar: 'تقويم العيادة الشهري',
    today: 'اليوم',
    previousMonth: 'الشهر السابق',
    nextMonth: 'الشهر التالي',
    event: 'موعد',
    task: 'مهمة',
    noItemsForDay: 'لا توجد مهام أو مواعيد',
    recentFollowups: 'آخر المتابعات',
    overrides: 'تغييرات البروتوكول',
    mare: 'الفرس',
    origin: 'مصدر الفرس',
    inside: 'داخلية',
    outside: 'خارجية',
    client: 'العميل',
    caseType: 'نوع الحالة',
    status: 'الحالة',
    startDate: 'بداية الدورة',
    owner: 'المالك',
    notes: 'ملاحظات',
    save: 'حفظ',
    cancel: 'إلغاء',
    selectCase: 'اختر حالة لعرض تفاصيلها.',
    noData: 'لا توجد بيانات.',
    examType: 'نوع الإجراء',
    examDate: 'تاريخ الإجراء',
    procedureType: 'نوع الإجراء',
    procedureDate: 'تاريخ الإجراء',
    follicle: 'حجم الجريب mm',
    follicleCount: 'عدد الجريبات',
    edema: 'وذمة الرحم',
    corpusLuteum: 'وجود الجسم الأصفر',
    uterineFluid: 'سوائل الرحم cm',
    cervixStatus: 'حالة عنق الرحم',
    cultureResult: 'نتيجة الزراعة',
    cytologyResult: 'نتيجة الخلايا',
    nextCheck: 'المتابعة القادمة',
    overrideReason: 'سبب تجاوز التوصية',
    pregnancyResult: 'نتيجة الحمل',
    fetalHeartbeat: 'نبض الجنين',
    twinDetected: 'وجود توأم',
    twinManagementDone: 'تم التعامل مع التوأم',
    progesterone: 'البروجسترون',
    vetDecision: 'قرار الطبيب',
    deleteExam: 'حذف الفحص',
    deleteExamConfirm: 'هل تريد حذف هذا الفحص من سجل الحالة؟ سيتم الحذف بشكل آمن وإعادة حساب حالة الحالة.',
    changeReason: 'سبب التغيير',
    value: 'القيمة',
    group: 'المجموعة',
    edit: 'تعديل',
    saved: 'تم الحفظ.',
    loadError: 'تعذر تحميل بيانات عيادة الإنتاج.',
    saveError: 'تعذر حفظ البيانات.',
    caseActions: 'إدارة الحالة',
    confirmedOvulation: 'الإباضة المؤكدة',
    expectedFoaling: 'موعد الولادة المتوقع',
    registeredMaresOnly: 'الحالات تسجل للأفراس المسجلة داخل النظام فقط.',
    autoDateHint: 'يتم حساب التاريخ تلقائياً من البروتوكول. اتركه فارغاً إلا إذا قرر الطبيب تعديله مع سبب واضح.',
    caseTypeLabels: {
      AI: 'تلقيح اصطناعي',
      'Frozen AI': 'تلقيح بسائل مجمد',
      'Embryo Transfer Donor': 'مانحة أجنة',
      'Embryo Transfer Recipient': 'مستقبلة أجنة',
      'Natural Cover': 'تلقيح طبيعي',
      'Pregnancy Follow-up': 'متابعة حمل',
    },
    caseStatusLabels: {
      Open: 'مفتوحة',
      'Waiting Ovulation': 'بانتظار الإباضة',
      Ovulated: 'تمّت الإباضة',
      Inseminated: 'ملقحة',
      Flushed: 'تم غسل الأجنة',
      Transferred: 'تم النقل',
      Pregnant: 'حامل',
      'Not Pregnant': 'غير حامل',
      Closed: 'مغلقة',
      Cancelled: 'ملغاة',
    },
    examTypeLabels: {
      Initial: 'فحص أولي',
      Ultrasound: 'سونار',
      'Pregnancy Check': 'فحص حمل',
      'Post Breeding': 'ما بعد التلقيح',
      'Recipient Sync': 'تزامن المستقبلة',
      'Foaling Check': 'فحص الولادة',
      Insemination: 'تلقيح',
    },
    pregnancyStatusLabels: {
      Pregnant: 'حامل',
      'Not Pregnant': 'غير حامل',
      'Twin Pregnancy': 'حمل توأمي',
      'Pregnancy Loss': 'فقدان حمل',
      Unclear: 'غير واضح',
    },
  },
  en: {
    title: 'Horse Reproduction Clinic',
    subtitle: 'Operational workflow for reproduction cases: exams, ovulation, AI, pregnancy, calendar, tasks, notifications, and editable clinical protocol.',
    disclaimer: 'These values are configurable clinical defaults and must be reviewed and approved by the responsible veterinarian. The system provides scheduling and documentation support only and does not replace clinical judgment.',
    dashboard: 'Dashboard',
    seasonFollowup: 'Season follow-up',
    seasonFollowupHint: 'Operational view matching the Embryos and Pregnancies workbook: mare, stallion, ovulation, heartbeat, P4, vaccinations, deworm, due date, and observations.',
    embryosSeason: 'Embryos season',
    pregnanciesSeason: 'Pregnancies season',
    caseSerial: 'Case #',
    mareRoom: 'R / location',
    stallion: 'Stallion',
    addStallion: 'Add stallion',
    stallionHint: 'Enter the stallion name even when semen comes from outside the stable. If the source is external, the provider or center is saved in the insemination notes.',
    selectStallion: 'Select stallion',
    externalStallionAllowed: 'Choose a registered stallion or type an external stallion/semen source name.',
    semenType: 'Semen type',
    inseminationDate: 'Insemination date',
    semenSource: 'Semen source',
    semenProvider: 'Semen provider / center',
    semenBatch: 'Inventory batch',
    dosesUsed: 'Doses',
    dosesWord: 'doses',
    noBatchesForStallion: 'No batches available for this stallion in inventory',
    batchHint: 'Deducted from semen inventory on save',
    doseDescription: 'Dose description',
    performedBy: 'Performed by',
    semenSourceLabels: {
      'In-house': 'In-house',
      External: 'External',
    },
    ovulationShort: 'OV',
    heartbeatShort: 'HB+',
    stopP4: 'STOP P4',
    vaccine5m: '5M',
    vaccine7m: '7M',
    vaccine9m: '9M',
    deworm: 'DEWORM',
    dueDate: 'DUE DATE',
    obs: 'OBS',
    amount: 'Amount',
    total: 'Total',
    paid: 'Paid',
    due: 'Due',
    cases: 'Cases',
    timeline: 'Timeline',
    calendar: 'Calendar',
    tasks: 'Tasks',
    notifications: 'Notifications',
    assignedTo: 'Assigned to',
    unassigned: 'Unassigned',
    markDone: 'Mark done',
    taskActions: 'Task actions',
    hubTitle: 'Reproduction clinic pages',
    rangeWeek: 'Week',
    goFlush: 'Flush embryos →',
    goTransfer: 'Transfer to a recipient →',
    goSync: 'Pair with a donor →',
    goFoaling: 'Record the foaling →',
    rangeMonth: 'Month',
    timelineScope: 'Timeline scope',
    thisCaseOnly: 'This case only',
    embryoTransfer: 'Embryo transfer',
    foalingRecords: 'Foalings and foals',
    recipientMares: 'Recipient mares',
    stallions: 'Stallions',
    semenInventory: 'Semen inventory',
    triggerHint: 'The system computes the 36–48 hour confirmation window and creates its task and calendar event automatically.',
    triggerDateTime: 'Trigger time',
    triggerMedication: 'Trigger medication',
    triggerDose: 'Dose',
    follicleSize: 'Follicle size (mm)',
    edemaGrade: 'Uterine edema grade',
    confirmHint: 'From this date the system computes the pregnancy check (+14), the expected foaling (+340) and the embryo flush window.',
    triggerRecord: 'Trigger record',
    confirmedOvulationDateTime: 'Confirmed ovulation time',
    alreadyConfirmed: 'confirmed',
    noTriggerYet: 'No trigger recorded for this case. Record an ovulation trigger first.',
    selectTriggerFirst: 'Select the trigger record first.',
    vaccineName: 'Vaccine',
    pregnancyMonth: 'Month of pregnancy (required)',
    pregnancyMonthRequired: 'Month of pregnancy is required.',
    vaccineDueDate: 'Due date',
    administeredDate: 'Administered on',
    followupDateTime: 'Follow-up time',
    followupType: 'Follow-up type',
    nextActionRequired: 'Requires a next action',
    nextActionDescription: 'Next action description',
    nextActionDue: 'Next action due',
    cancelTask: 'Cancel task',
    confirmCancelTask: 'Confirm cancel',
    cancelReason: 'Cancellation reason (required)',
    taskResult: 'Result (optional)',
    recordAction: 'Record the action in the case',
    taskCancelled: 'Task cancelled.',
    tasksWindowHint: 'Showing: due within 48 hours, and anything overdue.',
    noTasksDue: 'No tasks due right now.',
    overdue: 'Overdue',
    completionNote: 'Closing note (optional)',
    taskCompleted: 'Task completed.',
    saveFailed: 'Could not save.',
    timesheets: 'Timesheets',
    notificationHint: 'Notifications are now kept for exceptions only: overdue items, medical warnings, and critical alerts. Routine appointments live in the calendar.',
    selectHorse: 'Select horse',
    allHorses: 'All horses',
    timesheetHint: 'Timesheets measure vet and staff time by horse and case. They help review workload, time cost, and approvals before accounting.',
    timesheetTotalTime: 'Total time',
    timesheetBillableTime: 'Billable time',
    timesheetCost: 'Time cost',
    timesheetPending: 'Pending approval',
    horseTimelineHint: 'Choose a horse to show one timeline across all its reproduction cases.',
    openCase: 'Open case',
    addAction: 'Add procedure',
    scheduleAction: 'Calendar action',
    scheduledItem: 'Scheduled',
    taskItem: 'Task',
    calendarLegend: 'Calendar color guide',
    calendarAuto: 'Auto scheduled',
    calendarDoctor: 'Vet override',
    calendarManual: 'Manual event',
    calendarOverdue: 'Overdue',
    calendarWarning: 'Warning',
    calendarPregnancy: 'Pregnancy follow-up',
    protocol: 'Protocol',
    reports: 'Reports',
    newCase: 'New case',
    editCase: 'Edit case',
    deleteCase: 'Deactivate case',
    deleteCaseTitle: 'Deactivate reproduction case',
    deleteCaseConfirm: 'The case will be hidden from operations and calendars while keeping its record for audit. Enter the reason to continue.',
    deleteReason: 'Deactivation reason',
    deleted: 'Case deactivated safely.',
    addExam: 'Add procedure',
    refresh: 'Refresh',
    procedures: 'Procedures',
    legacyRecord: 'Legacy insemination',
    openCases: 'Open cases',
    waitingOvulation: 'Waiting ovulation',
    pregnantCases: 'Pregnant cases',
    foalingWatch: 'Foaling watch',
    schedule: 'Upcoming schedule',
    dueTasks: 'Due tasks',
    weeklyClinicPlan: 'Weekly clinic plan',
    monthlyClinicCalendar: 'Monthly clinic calendar',
    today: 'Today',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    event: 'Event',
    task: 'Task',
    noItemsForDay: 'No tasks or events',
    recentFollowups: 'Recent follow-ups',
    overrides: 'Protocol changes',
    mare: 'Mare',
    origin: 'Origin',
    inside: 'Inside',
    outside: 'Outside',
    client: 'Client',
    caseType: 'Case type',
    status: 'Status',
    startDate: 'Cycle start',
    owner: 'Owner',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    selectCase: 'Select a case to view details.',
    noData: 'No data.',
    examType: 'Procedure type',
    examDate: 'Procedure date',
    procedureType: 'Procedure type',
    procedureDate: 'Procedure date',
    follicle: 'Follicle size mm',
    follicleCount: 'Follicle count',
    edema: 'Uterine edema',
    corpusLuteum: 'Corpus luteum present',
    uterineFluid: 'Uterine fluid cm',
    cervixStatus: 'Cervix status',
    cultureResult: 'Culture result',
    cytologyResult: 'Cytology result',
    nextCheck: 'Next check',
    overrideReason: 'Override reason',
    pregnancyResult: 'Pregnancy result',
    fetalHeartbeat: 'Fetal heartbeat',
    twinDetected: 'Twin detected',
    twinManagementDone: 'Twin management done',
    progesterone: 'Progesterone',
    vetDecision: 'Vet decision',
    deleteExam: 'Delete exam',
    deleteExamConfirm: 'Delete this exam from the case record? It will be safely hidden and the case status will be recalculated.',
    changeReason: 'Change reason',
    value: 'Value',
    group: 'Group',
    edit: 'Edit',
    saved: 'Saved.',
    loadError: 'Failed to load reproduction clinic data.',
    saveError: 'Failed to save data.',
    caseActions: 'Case controls',
    confirmedOvulation: 'Confirmed ovulation',
    expectedFoaling: 'Expected foaling',
    registeredMaresOnly: 'Cases are registered for mares already recorded in the system only.',
    autoDateHint: 'The date is calculated from the protocol automatically. Leave it empty unless the veterinarian overrides it with a clear reason.',
    caseTypeLabels: {},
    caseStatusLabels: {},
    examTypeLabels: {},
    pregnancyStatusLabels: {},
  },
};

const DEFAULT_CASE_TYPES = [
  { case_type_key: 'AI', name_en: 'Artificial insemination', name_ar: 'تلقيح اصطناعي' },
  { case_type_key: 'Frozen AI', name_en: 'Frozen semen insemination', name_ar: 'تلقيح بسائل مجمد' },
  { case_type_key: 'Embryo Transfer Donor', name_en: 'Embryo transfer donor', name_ar: 'مانحة أجنة' },
  { case_type_key: 'Embryo Transfer Recipient', name_en: 'Embryo transfer recipient', name_ar: 'مستقبلة أجنة' },
  { case_type_key: 'Natural Cover', name_en: 'Natural cover', name_ar: 'تلقيح طبيعي' },
  { case_type_key: 'Pregnancy Follow-up', name_en: 'Pregnancy follow-up', name_ar: 'متابعة حمل' },
];

const DEFAULT_CASE_STATUSES = [
  { status_key: 'Open', name_en: 'Open', name_ar: 'مفتوحة' },
  { status_key: 'Waiting Ovulation', name_en: 'Waiting ovulation', name_ar: 'بانتظار الإباضة' },
  { status_key: 'Ovulated', name_en: 'Ovulated', name_ar: 'تمّت الإباضة' },
  { status_key: 'Inseminated', name_en: 'Inseminated', name_ar: 'ملقحة' },
  { status_key: 'Flushed', name_en: 'Flushed', name_ar: 'تم غسل الأجنة' },
  { status_key: 'Transferred', name_en: 'Transferred', name_ar: 'تم النقل' },
  { status_key: 'Pregnant', name_en: 'Pregnant', name_ar: 'حامل' },
  { status_key: 'Not Pregnant', name_en: 'Not pregnant', name_ar: 'غير حامل' },
  { status_key: 'Closed', name_en: 'Closed', name_ar: 'مغلقة' },
  { status_key: 'Cancelled', name_en: 'Cancelled', name_ar: 'ملغاة' },
];

const DEFAULT_EXAM_TYPES = [
  { exam_type_key: 'Initial', name_en: 'Initial examination', name_ar: 'فحص أولي' },
  { exam_type_key: 'Ultrasound', name_en: 'Ultrasound', name_ar: 'سونار' },
  INSEMINATION_ACTION_OPTION,
  { exam_type_key: 'Pregnancy Check', name_en: 'Pregnancy check', name_ar: 'فحص حمل' },
  { exam_type_key: 'Post Breeding', name_en: 'Post breeding', name_ar: 'ما بعد التلقيح' },
  { exam_type_key: 'Recipient Sync', name_en: 'Recipient synchronization', name_ar: 'تزامن المستقبلة' },
  { exam_type_key: 'Foaling Check', name_en: 'Foaling check', name_ar: 'فحص الولادة' },
];

const PREGNANCY_STATUSES = ['Pregnant', 'Not Pregnant', 'Twin Pregnancy', 'Pregnancy Loss', 'Unclear'];

const EXAM_FIELD_CONFIG = {
  Initial: ['follicle_size_mm', 'follicle_count', 'uterine_edema_grade', 'corpus_luteum_present', 'uterine_fluid_cm', 'cervix_status', 'progesterone_ng_ml', 'culture_result', 'cytology_result', 'vet_decision'],
  Ultrasound: ['follicle_size_mm', 'follicle_count', 'uterine_edema_grade', 'corpus_luteum_present', 'uterine_fluid_cm', 'cervix_status', 'vet_decision'],
  'Post Breeding': ['uterine_fluid_cm', 'cervix_status', 'culture_result', 'cytology_result', 'progesterone_ng_ml', 'vet_decision'],
  'Recipient Sync': ['follicle_size_mm', 'follicle_count', 'uterine_edema_grade', 'corpus_luteum_present', 'progesterone_ng_ml', 'vet_decision'],
  'Foaling Check': ['vet_decision'],
  [INSEMINATION_ACTION_KEY]: [],
};

function shouldShowExamField(examType, field) {
  return (EXAM_FIELD_CONFIG[examType] || EXAM_FIELD_CONFIG.Ultrasound).includes(field);
}

function todayInputDate() {
  return todayDateInput();
}

function nowInputDateTime() {
  return nowDateTimeInput();
}

function toInputDateTime(value) {
  return formatDateTimeInput(value);
}

function createEmptyCase() {
  return {
    mare_origin: 'Inside',
    inside_horse_id: '',
    outside_horse_id: '',
    client_id: '',
    case_type: 'AI',
    case_status: 'Open',
    cycle_start_date: todayInputDate(),
    notes: '',
  };
}

function createEmptyExam() {
  return {
    exam_type: 'Ultrasound',
    exam_date: nowInputDateTime(),
    follicle_size_mm: '',
    follicle_count: '',
    uterine_edema_grade: '',
    corpus_luteum_present: false,
    uterine_fluid_cm: '',
    progesterone_ng_ml: '',
    culture_result: 'Not Done',
    vet_decision: '',
    pregnancy_status: 'Unclear',
    fetal_heartbeat: false,
    twin_detected: false,
    twin_management_done: false,
    actual_next_check_date: '',
    override_reason: '',
    notes: '',
  };
}

function createEmptyInsemination() {
  return {
    insemination_date_time: nowInputDateTime(),
    semen_type: 'Fresh',
    stallion_id: '',
    stallion_name: '',
    semen_batch_id: '',
    doses_used: 1,
    semen_source: 'In-house',
    semen_provider: '',
    dose_description: '',
    performed_by: '',
    notes: '',
  };
}

function labelOf(map, value) {
  return map?.[value] || value || '-';
}

function horseName(row, lang = 'en') {
  if (!row) return '';
  return lang === 'ar'
    ? (row.name_arabic || row.name_ar || row.inside_horse_name_ar || row.horse_name_ar || row.name || row.inside_horse_name || row.horse_name || '')
    : (row.name || row.inside_horse_name || row.horse_name || row.name_arabic || row.name_ar || row.inside_horse_name_ar || row.horse_name_ar || '');
}

// رقم الشريحة (RFID) — أسماء حقول محتملة حسب المصدر.
function horseChip(row) {
  return row?.rfid_chip_number || row?.rfid || row?.chip_number || row?.microchip || '';
}

// تسمية تُظهر اسم الخيل (حسب اللغة) + رقم الشريحة إن وُجد.
function horseLabelWithChip(row, lang = 'en') {
  if (!row) return '';
  const name = horseName(row, lang);
  const chip = horseChip(row);
  return chip ? `${name} — ${chip}` : name;
}

// بحث موحّد: يطابق الاسم العربي + الإنجليزي + رقم الشريحة.
function filterHorseOptions(options, inputValue) {
  const q = String(inputValue || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => {
    const ar = (o.name_arabic || o.name_ar || o.inside_horse_name_ar || o.horse_name_ar || '').toLowerCase();
    const en = (o.name || o.inside_horse_name || o.horse_name || '').toLowerCase();
    const chip = String(horseChip(o)).toLowerCase();
    return ar.includes(q) || en.includes(q) || chip.includes(q);
  });
}

function toPositiveId(value) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

function langKey(language) {
  return String(language || '').startsWith('ar') ? 'ar' : 'en';
}

function formatDate(value) {
  return formatDateDisplay(value);
}

function formatDateTime(value) {
  return formatDateTimeDisplay(value);
}

function formatTime(value, lang = 'en') {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString(localeFor(lang), { hour: '2-digit', minute: '2-digit' });
}

function DateText({ value }) {
  return <span className="date-ltr">{formatDate(value)}</span>;
}

function DateTimeText({ value }) {
  return <span className="date-ltr">{formatDateTime(value)}</span>;
}

function toApiDate(date) {
  return formatDateInput(date);
}

function startOfDay(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function startOfWeek(date) {
  const value = startOfDay(date);
  const diffFromSaturday = value.getDay() === 6 ? 0 : value.getDay() + 1;
  value.setDate(value.getDate() - diffFromSaturday);
  return value;
}

function addDays(date, days) {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
}

function dateKey(date) {
  return toApiDate(date);
}

function monthRangeForApi(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: toApiDate(start), to: toApiDate(end) };
}

function localeFor(lang) {
  return lang === 'ar' ? 'ar-SA' : 'en-US';
}

function getScheduleDate(row) {
  return row?.start_date_time || row?.due_date_time || row?.followup_date_time || row?.event_date || row?.created_at || null;
}

function getScheduleTitle(row, lang) {
  const horseNameValue = lang === 'ar'
    ? (row?.horse_name_ar || row?.horse_name)
    : (row?.horse_name || row?.horse_name_ar);
  const action = lang === 'ar'
    ? (row?.title_ar || row?.task_title_ar || row?.title_en || row?.task_title_en || row?.event_type || row?.task_type)
    : (row?.title_en || row?.task_title_en || row?.title_ar || row?.task_title_ar || row?.event_type || row?.task_type);
  if (horseNameValue && action) return `${horseNameValue} - ${action}`;
  if (horseNameValue) return horseNameValue;
  if (lang === 'ar') {
    return row?.title_ar || row?.task_title_ar || row?.message_ar || row?.title_en || row?.task_title_en || row?.event_type || row?.task_type || '-';
  }
  return row?.title_en || row?.task_title_en || row?.message_en || row?.title_ar || row?.task_title_ar || row?.event_type || row?.task_type || '-';
}

function getScheduleHorse(row, lang) {
  return lang === 'ar'
    ? (row?.horse_name_ar || row?.horse_name || '')
    : (row?.horse_name || row?.horse_name_ar || '');
}

function getScheduleAction(row, lang) {
  if (lang === 'ar') return row?.title_ar || row?.task_title_ar || row?.title_en || row?.task_title_en || row?.event_type || row?.task_type || '';
  return row?.title_en || row?.task_title_en || row?.title_ar || row?.task_title_ar || row?.event_type || row?.task_type || '';
}

function getScheduleNote(row, lang) {
  const note = row?.notes || row?.completion_notes || row?.message_ar || row?.message_en;
  const status = row?.status || row?.case_status || row?.priority;
  const type = row?.event_type || row?.task_type || row?.case_type;
  const parts = [note, status, type].filter(Boolean);
  const value = parts.find((item) => String(item).trim());
  if (!value) return lang === 'ar' ? 'إجراء متابعة' : 'Follow-up action';
  return String(value).trim();
}

function scheduleKind(row) {
  return row?.task_id ? 'task' : 'event';
}

function scheduleVisual(row, text = {}) {
  const kind = row?._schedule_kind || scheduleKind(row);
  const status = row?.status || '';
  const priority = row?.priority || '';
  const sourceType = String(row?.source_type || '').toLowerCase();
  const eventType = String(row?.event_type || row?.task_type || '').toLowerCase();
  const noteText = String(row?.notes || row?.message_en || row?.message_ar || '').toLowerCase();

  if (status === 'Overdue' || priority === 'Critical') {
    return {
      key: 'overdue',
      label: text.calendarOverdue || 'Overdue',
      border: COLORS.rose,
      bg: COLORS.roseSoft,
      fg: COLORS.rose,
      accent: COLORS.rose,
    };
  }

  if (priority === 'High' || eventType.includes('emergency') || eventType.includes('warning')) {
    return {
      key: 'warning',
      label: text.calendarWarning || 'Warning',
      border: COLORS.warning,
      bg: COLORS.warningSoft,
      fg: COLORS.warning,
      accent: COLORS.warning,
    };
  }

  if (sourceType.includes('pregnancy') || sourceType.includes('expected_foaling') || eventType.includes('pregnancy') || eventType.includes('foaling')) {
    return {
      key: 'pregnancy',
      label: text.calendarPregnancy || 'Pregnancy follow-up',
      border: COLORS.green,
      bg: COLORS.greenSoft,
      fg: COLORS.green,
      accent: COLORS.green,
    };
  }

  if (noteText.includes('override') || noteText.includes('تعديل') || noteText.includes('تجاوز')) {
    return {
      key: 'doctor',
      label: text.calendarDoctor || 'Vet override',
      border: COLORS.gold,
      bg: COLORS.goldSoft,
      fg: COLORS.gold,
      accent: COLORS.gold,
    };
  }

  if (kind === 'task') {
    return {
      key: 'task',
      label: text.taskItem || 'Task',
      border: COLORS.gold,
      bg: COLORS.goldSoft,
      fg: COLORS.gold,
      accent: COLORS.gold,
    };
  }

  if (sourceType || row?.source_id) {
    return {
      key: 'auto',
      label: text.calendarAuto || 'Auto scheduled',
      border: COLORS.blue,
      bg: COLORS.blueSoft,
      fg: COLORS.blue,
      accent: COLORS.blue,
    };
  }

  return {
    key: 'manual',
    label: text.calendarManual || 'Manual event',
    border: COLORS.line,
    bg: COLORS.cardSoft,
    fg: COLORS.text,
    accent: COLORS.muted,
  };
}

function calendarLegendItems(text) {
  return [
    { key: 'auto', label: text.calendarAuto, visual: scheduleVisual({ source_type: 'protocol', source_id: 1 }, text) },
    { key: 'doctor', label: text.calendarDoctor, visual: scheduleVisual({ notes: 'override' }, text) },
    { key: 'pregnancy', label: text.calendarPregnancy, visual: scheduleVisual({ event_type: 'Pregnancy Check' }, text) },
    { key: 'warning', label: text.calendarWarning, visual: scheduleVisual({ priority: 'High' }, text) },
    { key: 'overdue', label: text.calendarOverdue, visual: scheduleVisual({ status: 'Overdue' }, text) },
  ];
}

function groupScheduleByDay(rows) {
  return (rows || []).reduce((acc, row) => {
    const value = getScheduleDate(row);
    if (!value) return acc;
    const key = dateKey(value);
    acc[key] = acc[key] || [];
    acc[key].push(row);
    return acc;
  }, {});
}

function StatCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 108 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: soft, color, display: 'grid', placeItems: 'center' }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value ?? 0}</Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function StatusChip({ value, label }) {
  const color = value === 'Pregnant' ? COLORS.green : value === 'Cancelled' || value === 'Closed' ? COLORS.muted : value === 'Waiting Ovulation' ? COLORS.warning : COLORS.blue;
  const soft = value === 'Pregnant' ? COLORS.greenSoft : value === 'Cancelled' || value === 'Closed' ? COLORS.cardSoft : value === 'Waiting Ovulation' ? COLORS.warningSoft : COLORS.blueSoft;
  return <Chip size="small" label={label || value || '-'} sx={{ bgcolor: soft, color, fontWeight: 900 }} />;
}

function Section({ title, action, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function SimpleList({ rows, render, emptyText }) {
  if (!rows?.length) return <Typography sx={{ color: COLORS.muted, py: 2 }}>{emptyText}</Typography>;
  return <Stack spacing={1}>{rows.map(render)}</Stack>;
}

export default function ReproductionPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = langKey(i18n.language);
  const translatedCopy = t('reproductionClinic', { returnObjects: true, defaultValue: COPY[lang] });
  const text = typeof translatedCopy === 'object' ? { ...COPY[lang], ...translatedCopy } : COPY[lang];
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState({});
  const [seasonFollowup, setSeasonFollowup] = useState({ embryos: [], pregnancies: [] });
  const [cases, setCases] = useState([]);
  const [caseTypeOptions, setCaseTypeOptions] = useState(DEFAULT_CASE_TYPES);
  const [caseStatusOptions, setCaseStatusOptions] = useState(DEFAULT_CASE_STATUSES);
  // قائمة الحالات داخل نموذج الحالة تُصفّى على نوع الحالة (3.5.5): لا «تم غسل الأجنة»
  // لمستقبلة ولا «حامل» لمانحة. أمّا caseStatusOptions فتبقى كاملة لأنّها مصدر تسميات
  // الصفوف في الجداول — تصفيتها تُفقد الحالات القديمة أسماءها.
  const [formStatusOptions, setFormStatusOptions] = useState(DEFAULT_CASE_STATUSES);
  const [examTypeOptions, setExamTypeOptions] = useState(DEFAULT_EXAM_TYPES);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [protocol, setProtocol] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [scheduleRange, setScheduleRange] = useState('week');
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [tasks, setTasks] = useState([]);
  const [triggerForm, setTriggerForm] = useState(createEmptyTrigger);
  const [confirmForm, setConfirmForm] = useState(createEmptyConfirm);
  const [vaccinationForm, setVaccinationForm] = useState(createEmptyVaccination);
  const [followupForm, setFollowupForm] = useState(createEmptyFollowupAction);
  const [taskDialog, setTaskDialog] = useState(null);
  const [taskMode, setTaskMode] = useState('complete');
  const [taskNote, setTaskNote] = useState('');
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetHorseId, setTimesheetHorseId] = useState('');
  const [timelineHorseId, setTimelineHorseId] = useState('');
  const [horseTimeline, setHorseTimeline] = useState([]);
  const [reports] = useState({ pregnancyRate: [], embryoRecovery: {} });
  const [mares, setMares] = useState([]);
  const [stallions, setStallions] = useState([]);
  const [semenBatches, setSemenBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [caseDialog, setCaseDialog] = useState(false);
  const [caseMode, setCaseMode] = useState('create');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [examDialog, setExamDialog] = useState(false);
  const [protocolDialog, setProtocolDialog] = useState(false);
  const [scheduleDialogRow, setScheduleDialogRow] = useState(null);
  const [caseForm, setCaseForm] = useState(createEmptyCase);
  const [examForm, setExamForm] = useState(createEmptyExam);
  const [inseminationForm, setInseminationForm] = useState(createEmptyInsemination);
  const [protocolForm, setProtocolForm] = useState({ parameter_id: '', parameter_value: '', change_reason: '' });
  const caseTypeLabels = useMemo(() => {
    const dynamicLabels = {};
    caseTypeOptions.forEach((item) => {
      dynamicLabels[item.case_type_key] = lang === 'ar' ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar);
    });
    return { ...(text.caseTypeLabels || {}), ...dynamicLabels };
  }, [caseTypeOptions, lang, text.caseTypeLabels]);
  useEffect(() => {
    if (!caseDialog) return undefined;
    let cancelled = false;
    api
      .get('/reproduction-clinic/case-statuses', { params: { case_type: caseForm.case_type } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = Array.isArray(data) && data.length ? data.filter((item) => Number(item.is_active ?? 1) === 1) : caseStatusOptions;
        // الحالة المحفوظة تبقى معروضة ولو خرجت عن النطاق، فلا يبدّلها الحفظ صامتًا.
        const current = caseStatusOptions.find((item) => item.status_key === caseForm.case_status);
        setFormStatusOptions(current && !rows.some((item) => item.status_key === current.status_key) ? [...rows, current] : rows);
      })
      .catch(() => setFormStatusOptions(caseStatusOptions));
    return () => {
      cancelled = true;
    };
  }, [caseDialog, caseForm.case_type, caseForm.case_status, caseStatusOptions]);

  // التنبيهات الحَرِجة والعالية وحدها، من حمولة اللوحة (تُعيدها غير مقروءة ومرتّبة
  // بالأولويّة، بحدّ عشرة). لا طلب شبكة إضافيّ بعد حذف التبويب.
  const criticalNotifications = useMemo(
    () => (dashboard.notifications || []).filter((row) => ['Critical', 'High'].includes(row.priority)),
    [dashboard.notifications]
  );

  const caseStatusLabels = useMemo(() => {
    const dynamicLabels = {};
    caseStatusOptions.forEach((item) => {
      dynamicLabels[item.status_key] = lang === 'ar' ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar);
    });
    return { ...(text.caseStatusLabels || {}), ...dynamicLabels };
  }, [caseStatusOptions, lang, text.caseStatusLabels]);
  const actionTypeOptions = useMemo(() => {
    const rows = Array.isArray(examTypeOptions) ? examTypeOptions : [];
    const withInsemination = rows.some((item) => item.exam_type_key === INSEMINATION_ACTION_KEY)
      ? rows
      : [...rows, INSEMINATION_ACTION_OPTION];
    // إجراءات البروتوكول تُضاف دائمًا: هي مسارات خادم مستقلّة لا أنواع فحص، فلا تأتي من
    // جدول أنواع الفحوصات ولا يصحّ أن يُخفيها تعديلٌ فيه.
    const missing = PROTOCOL_ACTION_OPTIONS.filter((opt) => !withInsemination.some((item) => item.exam_type_key === opt.exam_type_key));
    return [...withInsemination, ...missing];
  }, [examTypeOptions]);
  const examTypeLabels = useMemo(() => {
    const dynamicLabels = {};
    actionTypeOptions.forEach((item) => {
      dynamicLabels[item.exam_type_key] = lang === 'ar' ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar);
    });
    return { ...(text.examTypeLabels || {}), ...dynamicLabels };
  }, [actionTypeOptions, lang, text.examTypeLabels]);
  const pregnancyStatusLabels = text.pregnancyStatusLabels || {};

  const selectedCase = useMemo(
    () => cases.find((row) => Number(row.case_id) === Number(selectedCaseId)) || cases[0] || null,
    [cases, selectedCaseId],
  );

  const caseNextAction = useMemo(() => nextCaseAction(caseDetails?.case || selectedCase), [caseDetails, selectedCase]);

  const loadDetails = async (caseId) => {
    const safeCaseId = toPositiveId(caseId);
    if (!safeCaseId) return setCaseDetails(null);
    const { data } = await api.get(`/reproduction-clinic/cases/${safeCaseId}`);
    setCaseDetails(data);
  };

  const loadData = async (preferredCaseId = selectedCaseId) => {
    setLoading(true);
    setError('');
    try {
      const [
        dashboardRes,
        seasonRes,
        caseTypesRes,
        caseStatusesRes,
        examTypesRes,
        casesRes,
        protocolRes,
        calendarRes,
        tasksRes,
        maresRes,
        stallionsRes,
      ] = await Promise.allSettled([
        api.get('/reproduction-clinic/dashboard'),
        api.get('/reproduction-clinic/season-followup'),
        api.get('/reproduction-clinic/case-types'),
        api.get('/reproduction-clinic/case-statuses'),
        api.get('/reproduction-clinic/exam-types'),
        api.get('/reproduction-clinic/cases'),
        api.get('/reproduction-clinic/protocol-parameters'),
        api.get('/reproduction-clinic/calendar-events', { params: monthRangeForApi(calendarMonth) }),
        api.get('/reproduction-clinic/tasks', { params: { window_hours: TASK_WINDOW_HOURS } }),
        api.get('/mares'),
        api.get('/stallionslist'),
      ]);

      const get = (res, fallback) => (res.status === 'fulfilled' ? res.value.data : fallback);
      const caseRows = get(casesRes, []);
      setDashboard(get(dashboardRes, {}));
      setSeasonFollowup(get(seasonRes, { embryos: [], pregnancies: [] }) || { embryos: [], pregnancies: [] });
      const typeRows = get(caseTypesRes, DEFAULT_CASE_TYPES);
      setCaseTypeOptions(Array.isArray(typeRows) && typeRows.length ? typeRows.filter((item) => Number(item.is_active ?? 1) === 1) : DEFAULT_CASE_TYPES);
      const statusRows = get(caseStatusesRes, DEFAULT_CASE_STATUSES);
      setCaseStatusOptions(Array.isArray(statusRows) && statusRows.length ? statusRows.filter((item) => Number(item.is_active ?? 1) === 1) : DEFAULT_CASE_STATUSES);
      const examRows = get(examTypesRes, DEFAULT_EXAM_TYPES);
      setExamTypeOptions(Array.isArray(examRows) && examRows.length ? examRows.filter((item) => Number(item.is_active ?? 1) === 1) : DEFAULT_EXAM_TYPES);
      setCases(Array.isArray(caseRows) ? caseRows : []);
      setProtocol(get(protocolRes, []));
      setCalendar(get(calendarRes, []));
      setTasks(get(tasksRes, []));
      const mareRows = get(maresRes, []);
      setMares(Array.isArray(mareRows) ? mareRows.filter((mare) => !mare.gender || mare.gender === 'M') : []);
      const stallionRows = get(stallionsRes, []);
      setStallions(Array.isArray(stallionRows) ? stallionRows : []);

      const activeCase = toPositiveId(preferredCaseId) || toPositiveId(caseRows?.[0]?.case_id);
      setSelectedCaseId(activeCase || null);
      if (activeCase) await loadDetails(activeCase);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || text.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab !== 'calendar') return;
    const loadMonth = async () => {
      try {
        const { data } = await api.get('/reproduction-clinic/calendar-events', { params: monthRangeForApi(calendarMonth) });
        setCalendar(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || text.loadError);
      }
    };
    loadMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth, tab]);

  const openCase = async (caseId) => {
    const safeCaseId = toPositiveId(caseId);
    if (!safeCaseId) return;
    setSelectedCaseId(safeCaseId);
    try {
      await loadDetails(safeCaseId);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || text.loadError);
    }
  };

  const loadHorseTimeline = async (horseId) => {
    setTimelineHorseId(horseId);
    if (!horseId) {
      setHorseTimeline([]);
      return;
    }
    try {
      const { data } = await api.get(`/reproduction-clinic/horses/${horseId}/timeline`);
      setHorseTimeline(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || text.loadError);
    }
  };

  const openScheduleAction = (row) => {
    if (!row?.case_id) return;
    setScheduleDialogRow(row);
  };

  const openScheduleCase = async () => {
    if (!scheduleDialogRow?.case_id) return;
    await openCase(scheduleDialogRow.case_id);
    setScheduleDialogRow(null);
    setTab('cases');
  };

  const addExamFromSchedule = async () => {
    if (!scheduleDialogRow?.case_id) return;
    await openCase(scheduleDialogRow.case_id);
    setScheduleDialogRow(null);
    setExamForm(createEmptyExam());
    setInseminationForm(createEmptyInsemination());
    setExamDialog(true);
  };

  const openProcedureDialog = () => {
    setExamForm(createEmptyExam());
    setInseminationForm(createEmptyInsemination());
    setExamDialog(true);
  };

  const openNewCaseDialog = () => {
    setCaseMode('create');
    setCaseForm(createEmptyCase());
    setCaseDialog(true);
  };

  const openEditCaseDialog = (row) => {
    if (!row) return;
    setCaseMode('edit');
    setCaseForm({
      mare_origin: 'Inside',
      inside_horse_id: row.inside_horse_id || '',
      outside_horse_id: '',
      client_id: '',
      case_type: row.case_type || 'AI',
      case_status: row.case_status || 'Open',
      cycle_start_date: row.cycle_start_date ? String(row.cycle_start_date).slice(0, 10) : todayInputDate(),
      confirmed_ovulation_date: toInputDateTime(row.confirmed_ovulation_date),
      expected_foaling_date: row.expected_foaling_date ? String(row.expected_foaling_date).slice(0, 10) : '',
      assigned_vet_id: row.assigned_vet_id || '',
      notes: row.notes || '',
      change_reason: '',
    });
    setCaseDialog(true);
  };

  const saveCase = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (caseMode === 'edit' && selectedCase) {
        await api.put(`/reproduction-clinic/cases/${selectedCase.case_id}`, caseForm);
      } else {
        await api.post('/reproduction-clinic/cases', caseForm);
      }
      setCaseDialog(false);
      setCaseForm(createEmptyCase());
      setMessage(text.saved);
      await loadData(caseMode === 'edit' && selectedCase?.case_id ? selectedCase.case_id : undefined);
      if (caseMode === 'edit' && selectedCase?.case_id) await loadDetails(selectedCase.case_id);
    } catch (err) {
      setError(err?.response?.data?.errors?.join(', ') || err?.response?.data?.message || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  const deleteCase = async () => {
    if (!selectedCase) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`/reproduction-clinic/cases/${selectedCase.case_id}`, { data: { reason: deleteReason } });
      setDeleteDialog(false);
      setDeleteReason('');
      setSelectedCaseId(null);
      setCaseDetails(null);
      setMessage(text.deleted);
      await loadData(null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  const saveExam = async () => {
    if (!selectedCase) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (examForm.exam_type === OVULATION_TRIGGER_KEY) {
        // الخادم يحسب نافذة التأكيد (٣٦–٤٨ ساعة) ويُنشئ مهمّتها وحدث تقويمها في معاملة
        // واحدة. لا ترسل النافذة من هنا: مرجعٌ واحد للحساب لا اثنان.
        await api.post('/reproduction-clinic/ovulation-trigger', {
          ...triggerForm,
          case_id: selectedCase.case_id,
          follicle_size_at_trigger_mm: triggerForm.follicle_size_at_trigger_mm || null,
          uterine_edema_grade: triggerForm.uterine_edema_grade || null,
        });
      } else if (examForm.exam_type === OVULATION_CONFIRM_KEY) {
        if (!confirmForm.ovulation_id) {
          setError(text.selectTriggerFirst);
          setSaving(false);
          return;
        }
        // من هنا تُشتقّ مواعيد الحمل والولادة ونافذة الغسل — وهو ما كان مقطوعًا.
        await api.post('/reproduction-clinic/ovulation-confirmation', {
          ovulation_id: Number(confirmForm.ovulation_id),
          confirmed_ovulation_date_time: confirmForm.confirmed_ovulation_date_time,
        });
      } else if (examForm.exam_type === VACCINATION_KEY) {
        // شهر الحمل NOT NULL في الجدول: إرساله فارغًا يُسقط الحفظ برسالة قاعدة بيانات
        // لا يفهمها المستخدم. يُمنَع هنا برسالة مفهومة.
        if (!String(vaccinationForm.due_month_of_pregnancy).trim()) {
          setError(text.pregnancyMonthRequired);
          setSaving(false);
          return;
        }
        await api.post('/reproduction-clinic/vaccinations', {
          ...vaccinationForm,
          case_id: selectedCase.case_id,
          due_month_of_pregnancy: Number(vaccinationForm.due_month_of_pregnancy),
          due_date: vaccinationForm.due_date || null,
          administered_date: vaccinationForm.administered_date || null,
        });
      } else if (examForm.exam_type === FOLLOWUP_KEY) {
        await api.post('/reproduction-clinic/followups', {
          ...followupForm,
          case_id: selectedCase.case_id,
          next_action_due_date: followupForm.next_action_required ? followupForm.next_action_due_date || null : null,
          next_action_description: followupForm.next_action_required ? followupForm.next_action_description || null : null,
        });
      } else if (examForm.exam_type === INSEMINATION_ACTION_KEY) {
        const providerNote = inseminationForm.semen_source === 'External' && inseminationForm.semen_provider.trim()
          ? `External semen provider: ${inseminationForm.semen_provider.trim()}`
          : '';
        const notes = [providerNote, inseminationForm.notes].filter((item) => String(item || '').trim()).join('\n');
        await api.post('/reproduction-clinic/inseminations', {
          ...inseminationForm,
          case_id: selectedCase.case_id,
          insemination_date_time: inseminationForm.insemination_date_time || examForm.exam_date,
          insemination_number: 1,
          notes,
        });
      } else if (examForm.exam_type === 'Pregnancy Check') {
        await api.post('/reproduction-clinic/pregnancy-checks', {
          case_id: selectedCase.case_id,
          check_date: examForm.exam_date,
          check_type: 'First Pregnancy Check',
          pregnancy_status: examForm.pregnancy_status,
          fetal_heartbeat: examForm.fetal_heartbeat,
          twin_detected: examForm.twin_detected,
          twin_management_done: examForm.twin_management_done,
          progesterone_ng_ml: examForm.progesterone_ng_ml,
          vet_decision: examForm.vet_decision,
          actual_next_check_date: examForm.actual_next_check_date,
          notes: examForm.notes,
        });
      } else {
        await api.post('/reproduction-clinic/examinations', { ...examForm, case_id: selectedCase.case_id });
      }
      setExamDialog(false);
      setExamForm(createEmptyExam());
      setInseminationForm(createEmptyInsemination());
      setTriggerForm(createEmptyTrigger());
      setConfirmForm(createEmptyConfirm());
      setVaccinationForm(createEmptyVaccination());
      setFollowupForm(createEmptyFollowupAction());
      setMessage(text.saved);
      await loadDetails(selectedCase.case_id);
      await loadData(selectedCase.case_id);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || text.saveError);
    } finally {
      setSaving(false);
    }
  };


  // ---- المهامّ: الإنجاز والإلغاء (3.6.6) ---------------------------------
  // أُسقط الإسناد إلى طبيب بعينه: المناوب هو من ينفّذ، فالإسناد حقلٌ يُملأ ولا يُقرأ.
  // والمطلوب أحد أمرين: أنجزها وسجّل النتيجة، أو ألغِها وقل لماذا.

  const openTaskDialog = (row) => {
    setTaskDialog(row);
    setTaskMode('complete');
    setTaskNote('');
  };

  /** يفتح حالة المهمّة ونموذج الإجراء فيها — النتيجة السريريّة توثَّق في الحالة. */
  const openTaskCaseAction = async () => {
    if (!taskDialog?.case_id) return;
    await openCase(taskDialog.case_id);
    setTaskDialog(null);
    setTab('cases');
    setExamForm(createEmptyExam());
    setInseminationForm(createEmptyInsemination());
    setExamDialog(true);
  };

  const submitTaskDialog = async () => {
    if (!taskDialog) return;
    const cancelling = taskMode === 'cancel';
    if (cancelling && !taskNote.trim()) return;
    try {
      // وقت الإغلاق يكتبه الخادم لا هذه الشاشة.
      await api.put(`/reproduction-clinic/tasks/${taskDialog.task_id}`, {
        status: cancelling ? 'Cancelled' : 'Completed',
        completion_notes: taskNote.trim() || null,
      });
      setTaskDialog(null);
      setMessage(cancelling ? text.taskCancelled : text.taskCompleted);
      await loadData(selectedCase?.case_id);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || text.saveFailed);
    }
  };

  const deleteTimelineRecord = async (row) => {
    if (!row?.source_id || !selectedCase) return;
    const endpoint =
      row.type === 'Examination'
        ? `/reproduction-clinic/examinations/${row.source_id}`
        : row.type === 'Pregnancy Check'
          ? `/reproduction-clinic/pregnancy-checks/${row.source_id}`
          : null;
    if (!endpoint) return;
    if (!window.confirm(text.deleteExamConfirm)) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(endpoint, { data: { reason: text.deleteExam } });
      setMessage(text.saved);
      await loadDetails(selectedCase.case_id);
      await loadData(selectedCase.case_id);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  const saveProtocol = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.put(`/reproduction-clinic/protocol-parameters/${protocolForm.parameter_id}`, {
        parameter_value: protocolForm.parameter_value,
        change_reason: protocolForm.change_reason,
      });
      setProtocolDialog(false);
      setMessage(text.saved);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.errors?.join(', ') || err?.response?.data?.message || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  const summary = dashboard.summary || {};
  const weeklyDashboardItems = useMemo(() => ([
    ...(dashboard.calendar_events || []).map((row) => ({ ...row, _schedule_kind: 'event' })),
    ...(dashboard.tasks || []).map((row) => ({ ...row, _schedule_kind: 'task' })),
  ]), [dashboard.calendar_events, dashboard.tasks]);

  return (
    <Box className="smart-page-container" sx={{ bgcolor: COLORS.page, minHeight: '100%', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5, maxWidth: 980 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800 }}>{text.refresh}</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewCaseDialog} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.newCase}</Button>
          </Stack>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>{text.disclaimer}</Alert>
      {loading && <CircularProgress size={28} sx={{ my: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}><StatCard icon={<FavoriteIcon />} title={text.openCases} value={summary.open_cases || 0} helper={text.status} /></Grid>
        <Grid item xs={12} md={3}><StatCard icon={<ScienceIcon />} title={text.waitingOvulation} value={summary.waiting_ovulation || 0} helper={text.schedule} color={COLORS.warning} soft={COLORS.warningSoft} /></Grid>
        <Grid item xs={12} md={3}><StatCard icon={<AssignmentTurnedInIcon />} title={text.pregnantCases} value={summary.pregnant_cases || 0} helper={text.cases} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>
        <Grid item xs={12} md={3}><StatCard icon={<NotificationsActiveIcon />} title={text.foalingWatch} value={summary.foaling_watch || 0} helper={text.notifications} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
      </Grid>

      <ReproHub text={text} navigate={navigate} />

      <Paper elevation={0} sx={{ ...cardSx, mb: 2.5 }}>
        <Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 1 }}>
          {[
            ['dashboard', text.dashboard, <ScienceIcon />],
            ['season', text.seasonFollowup, <AssignmentTurnedInIcon />],
            ['cases', text.cases, <FavoriteIcon />],
            ['tasks', text.tasks, <AssignmentTurnedInIcon />],
            ['protocol', text.protocol, <SettingsIcon />],
            ['reports', text.reports, <ScienceIcon />],
          ].filter(([value]) => !HIDDEN_OPERATIONAL_TABS.has(value)).map(([value, label, icon]) => (
            <Tab key={value} value={value} icon={icon} iconPosition="start" label={label} sx={{ fontWeight: 900 }} />
          ))}
        </Tabs>
      </Paper>

      {tab === 'dashboard' && (
        <Grid container spacing={2.5}>
          {/* 3.6.3 — حُذف تبويب «الإشعارات»، لكنّ جدول reproduction_notifications لا يقرؤه
              أحد غيره في الواجهة: جرس الشريط العلويّ يشتقّ تنبيهاته من أحداث التقويم لا من
              هذا الجدول. فحذف التبويب وحده كان يُخفي تنبيه «حمل توأمي» الحَرِج نهائيًّا.
              يبقى هنا الحَرِج والعالي فقط — وهو ما يستدعي تدخّلًا الآن — من حمولة اللوحة
              نفسها بلا طلب إضافيّ. */}
          {criticalNotifications.length > 0 && (
            <Grid item xs={12}><Section title={text.notifications}><NotificationList rows={criticalNotifications} empty={text.noData} /></Section></Grid>
          )}
          {/* 3.7.2 — «لوحة المتابعة» و«التقويم» كانا تبويبين يعرضان المواعيد نفسها بمدّتين:
              الأسبوع والشهر. صارا تبويبًا واحدًا بمفتاح مدّة — فالسؤال «ماذا عندي؟» واحد،
              والمدّة تفصيلٌ فيه لا سببٌ لشاشة ثانية. */}
          <Grid item xs={12}>
            <Section
              title={scheduleRange === 'week' ? text.weeklyClinicPlan : text.monthlyClinicCalendar}
              action={(
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={scheduleRange}
                  onChange={(_event, value) => value && setScheduleRange(value)}
                >
                  <ToggleButton value="week" sx={{ fontWeight: 900 }}>{text.rangeWeek}</ToggleButton>
                  <ToggleButton value="month" sx={{ fontWeight: 900 }}>{text.rangeMonth}</ToggleButton>
                </ToggleButtonGroup>
              )}
            >
              {scheduleRange === 'week'
                ? <WeeklySchedule rows={weeklyDashboardItems} text={text} lang={lang} onOpen={openScheduleAction} />
                : <MonthlyCalendar rows={calendar} month={calendarMonth} onMonthChange={setCalendarMonth} text={text} lang={lang} onOpen={openScheduleAction} />}
            </Section>
          </Grid>
          <Grid item xs={12} md={6}><Section title={text.recentFollowups}><FollowupList rows={dashboard.followups} empty={text.noData} /></Section></Grid>
          <Grid item xs={12} md={6}><Section title={text.overrides}><ProtocolChangeList rows={dashboard.protocol_overrides} empty={text.noData} /></Section></Grid>
        </Grid>
      )}

      {tab === 'season' && (
        <Section title={text.seasonFollowup}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text }}>{text.seasonFollowupHint}</Alert>
            <SeasonFollowupTable
              title={text.embryosSeason}
              rows={seasonFollowup.embryos || []}
              text={text}
            />
            <SeasonFollowupTable
              title={text.pregnanciesSeason}
              rows={seasonFollowup.pregnancies || []}
              text={text}
            />
          </Stack>
        </Section>
      )}

      {tab === 'cases' && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={7}>
            <Section title={text.cases} action={<Chip label={cases.length} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900 }} />}>
              <SimpleList rows={cases} emptyText={text.noData} render={(row) => (
                <Paper key={row.case_id} elevation={0} onClick={() => openCase(row.case_id)} sx={{ p: 1.5, border: `1px solid ${Number(selectedCase?.case_id) === Number(row.case_id) ? COLORS.gold : COLORS.line}`, borderRadius: 2, cursor: 'pointer', bgcolor: Number(selectedCase?.case_id) === Number(row.case_id) ? COLORS.goldSoft : COLORS.card }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.inside_horse_name_ar || row.inside_horse_name || '-'}</Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>#{row.case_id} · {row.owner_name || '-'} · <DateText value={row.cycle_start_date} /></Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusChip value={row.case_status} label={labelOf(caseStatusLabels, row.case_status)} />
                      <Chip size="small" label={labelOf(caseTypeLabels, row.case_type)} sx={{ bgcolor: COLORS.cardSoft, fontWeight: 800 }} />
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); setSelectedCaseId(row.case_id); openEditCaseDialog(row); }} sx={{ color: COLORS.blue }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Paper>
              )} />
            </Section>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Section
              title={selectedCase ? `${text.timeline}: ${selectedCase.inside_horse_name_ar || selectedCase.inside_horse_name || ''}` : text.timeline}
              action={(
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {caseNextAction && hasActionPermission(caseNextAction.page) && (
                    <Button onClick={() => navigate(caseNextAction.path)} startIcon={<ArrowForwardIcon />} sx={{ color: COLORS.green, fontWeight: 900 }}>
                      {text[caseNextAction.key]}
                    </Button>
                  )}
                  <Button disabled={!selectedCase} onClick={() => openEditCaseDialog(selectedCase)} startIcon={<EditIcon />} sx={{ color: COLORS.blue, fontWeight: 900 }}>{text.edit}</Button>
                  <Button disabled={!selectedCase} onClick={() => setDeleteDialog(true)} startIcon={<DeleteOutlineIcon />} sx={{ color: COLORS.rose, fontWeight: 900 }}>{text.deleteCase}</Button>
                  <Button disabled={!selectedCase} onClick={openProcedureDialog} startIcon={<AddIcon />} sx={{ color: COLORS.gold, fontWeight: 900 }}>{text.addAction}</Button>
                </Stack>
              )}
            >
              <Stack spacing={1.5}>
                {/* 3.7.2 — كان «الخط الزمني» تبويبًا مستقلًّا يعرض CaseSummary وTimelineList
                    والأزرار الثلاثة نفسها الموجودة هنا حرفيًّا. زيادته الوحيدة منتقي الفرس
                    لعرض خطّها عبر كلّ حالاتها — فانتقل إلى هنا خيارَ عرض، وذهب التبويب.
                    تبويبان يعرضان الشيء نفسه يجعلان المستخدم يتنقّل بينهما بحثًا عن فرق
                    غير موجود. */}
                <Autocomplete
                  size="small"
                  options={[{ horse_id: '', _current: true }, ...mares]}
                  value={mares.find((mare) => String(mare.horse_id) === String(timelineHorseId)) || { horse_id: '', _current: true }}
                  onChange={(_event, option) => loadHorseTimeline(option?.horse_id || '')}
                  getOptionLabel={(option) => (
                    option?._current
                      ? (selectedCase ? `${text.thisCaseOnly}` : text.allHorses)
                      : horseName(option, lang)
                  )}
                  isOptionEqualToValue={(option, value) => String(option.horse_id || '') === String(value.horse_id || '')}
                  renderInput={(params) => <TextField {...params} label={text.timelineScope} />}
                />
                {timelineHorseId
                  ? <Alert severity="info" sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text }}>{text.horseTimelineHint}</Alert>
                  : <CaseSummary row={caseDetails?.case || selectedCase} text={text} caseStatusLabels={caseStatusLabels} />}
                <TimelineList
                  rows={timelineHorseId ? horseTimeline : caseDetails?.timeline}
                  empty={selectedCase || timelineHorseId ? text.noData : text.selectCase}
                  onDelete={!timelineHorseId ? deleteTimelineRecord : undefined}
                  deleteLabel={text.deleteExam}
                  text={text}
                  lang={lang}
                />
              </Stack>
            </Section>
          </Grid>
        </Grid>
      )}

      {tab === 'tasks' && (
        <Section title={text.tasks}>
          <Alert severity="info" sx={{ mb: 2, bgcolor: COLORS.blueSoft, color: COLORS.text }}>{text.tasksWindowHint}</Alert>
          <TaskList rows={tasks} empty={text.noTasksDue} onOpen={openTaskDialog} text={text} lang={lang} />
        </Section>
      )}
      {tab === 'timesheets' && (
        <Section title={text.timesheets}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text }}>{text.timesheetHint}</Alert>
            <Autocomplete
              options={mares}
              sx={{ maxWidth: 420 }}
              value={mares.find((m) => String(m.horse_id) === String(timesheetHorseId)) || null}
              onChange={async (_event, option) => {
                const value = option?.horse_id || '';
                setTimesheetHorseId(value);
                const { data } = await api.get('/reproduction-clinic/timesheets', { params: value ? { horse_id: value } : {} });
                setTimesheets(Array.isArray(data) ? data : []);
              }}
              getOptionLabel={(option) => horseLabelWithChip(option, lang)}
              filterOptions={(opts, state) => filterHorseOptions(opts, state.inputValue)}
              isOptionEqualToValue={(option, value) => String(option.horse_id) === String(value.horse_id)}
              renderOption={(props, option) => (
                <li {...props} key={option.horse_id}>{horseLabelWithChip(option, lang)}</li>
              )}
              renderInput={(params) => <TextField {...params} label={text.selectHorse} placeholder={text.allHorses} />}
            />
            <TimesheetSummary rows={timesheets} text={text} />
            <TimesheetList rows={timesheets} empty={text.noData} />
          </Stack>
        </Section>
      )}
      {tab === 'protocol' && (
        <Section title={text.protocol}>
          <SimpleList rows={protocol} emptyText={text.noData} render={(row) => (
            <Paper key={row.parameter_id} elevation={0} sx={{ p: 1.5, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.card }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1}>
                <Box>
                  <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{lang === 'ar' ? row.parameter_name_ar : row.parameter_name_en}</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.parameter_key} · {row.parameter_group}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`${row.parameter_value} ${row.parameter_unit || ''}`} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900 }} />
                  <Button size="small" onClick={() => { setProtocolForm({ parameter_id: row.parameter_id, parameter_value: row.parameter_value, change_reason: '' }); setProtocolDialog(true); }} sx={{ color: COLORS.gold, fontWeight: 900 }}>{text.edit}</Button>
                </Stack>
              </Stack>
            </Paper>
          )} />
        </Section>
      )}
      {tab === 'reports' && <Reports reports={reports} text={text} />}

      {/* قائمة خيارات المهمّة — على نمط نافذة الإجراء المجدول في «الحالات»: البطاقة نفسها
          ثمّ الخيارات. الإنجاز والإلغاء كلاهما يُغلق المهمّة، والفرق أنّ الإلغاء يسأل عن
          السبب إلزامًا: «لم تعد هناك حاجة» بلا سبب لا تُقرأ بعد ستّة أشهر. */}
      <Dialog open={!!taskDialog} onClose={() => setTaskDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.taskActions}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 900 }}>
            {taskDialog ? (lang === 'ar' ? (taskDialog.task_title_ar || taskDialog.task_title_en) : (taskDialog.task_title_en || taskDialog.task_title_ar)) : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 1.5 }}>
            <DateTimeText value={taskDialog?.due_date_time} />
            {(taskDialog?.horse_name_ar || taskDialog?.horse_name) ? ` · ${lang === 'ar' ? (taskDialog?.horse_name_ar || taskDialog?.horse_name) : (taskDialog?.horse_name || taskDialog?.horse_name_ar)}` : ''}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Button size="small" variant={taskMode === 'complete' ? 'contained' : 'outlined'} onClick={() => setTaskMode('complete')}>
              {text.markDone}
            </Button>
            <Button size="small" color="error" variant={taskMode === 'cancel' ? 'contained' : 'outlined'} onClick={() => setTaskMode('cancel')}>
              {text.cancelTask}
            </Button>
          </Stack>

          <TextField
            label={taskMode === 'cancel' ? text.cancelReason : text.taskResult}
            value={taskNote}
            onChange={(event) => setTaskNote(event.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          {/* تسجيل الإجراء يفتح نموذج الحالة نفسه: النتيجة السريريّة توثَّق حيث تُقرأ
              لاحقًا — في الحالة — لا في ملاحظة مهمّة. */}
          <Button onClick={openTaskCaseAction} disabled={!taskDialog?.case_id} sx={{ color: COLORS.blue, fontWeight: 900 }}>
            {text.recordAction}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setTaskDialog(null)}>{text.cancel}</Button>
            <Button
              variant="contained"
              color={taskMode === 'cancel' ? 'error' : 'primary'}
              onClick={submitTaskDialog}
              disabled={taskMode === 'cancel' && !taskNote.trim()}
            >
              {taskMode === 'cancel' ? text.confirmCancelTask : text.markDone}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={caseDialog} onClose={() => setCaseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{caseMode === 'edit' ? text.editCase : text.newCase}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">{text.registeredMaresOnly}</Alert>
            <Autocomplete
              options={mares}
              disabled={caseMode === 'edit'}
              value={mares.find((m) => String(m.horse_id) === String(caseForm.inside_horse_id)) || null}
              onChange={(_event, option) => setCaseForm((v) => ({ ...v, inside_horse_id: option?.horse_id || '', mare_origin: 'Inside' }))}
              getOptionLabel={(option) => horseLabelWithChip(option, lang)}
              filterOptions={(opts, state) => filterHorseOptions(opts, state.inputValue)}
              isOptionEqualToValue={(option, value) => String(option.horse_id) === String(value.horse_id)}
              renderOption={(props, option) => (
                <li {...props} key={option.horse_id}>{horseLabelWithChip(option, lang)}</li>
              )}
              fullWidth
              renderInput={(params) => <TextField {...params} label={text.mare} />}
            />
            <TextField select label={text.caseType} value={caseForm.case_type} onChange={(e) => setCaseForm((v) => ({ ...v, case_type: e.target.value }))} fullWidth>
              {caseTypeOptions.map((item) => (
                <MenuItem key={item.case_type_key} value={item.case_type_key}>
                  {labelOf(caseTypeLabels, item.case_type_key)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select label={text.status} value={caseForm.case_status} onChange={(e) => setCaseForm((v) => ({ ...v, case_status: e.target.value }))} fullWidth>
              {formStatusOptions.map((item) => (
                <MenuItem key={item.status_key} value={item.status_key}>
                  {labelOf(caseStatusLabels, item.status_key)}
                </MenuItem>
              ))}
            </TextField>
            <TextField type="date" label={text.startDate} helperText={text.autoDateHint} InputLabelProps={{ shrink: true }} value={caseForm.cycle_start_date} onChange={(e) => setCaseForm((v) => ({ ...v, cycle_start_date: e.target.value }))} fullWidth />
            {caseMode === 'edit' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField type="datetime-local" label={text.confirmedOvulation || 'Confirmed ovulation'} InputLabelProps={{ shrink: true }} value={caseForm.confirmed_ovulation_date || ''} onChange={(e) => setCaseForm((v) => ({ ...v, confirmed_ovulation_date: e.target.value }))} fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField type="date" label={text.expectedFoaling || 'Expected foaling'} InputLabelProps={{ shrink: true }} value={caseForm.expected_foaling_date || ''} onChange={(e) => setCaseForm((v) => ({ ...v, expected_foaling_date: e.target.value }))} fullWidth />
                </Grid>
              </Grid>
            )}
            <TextField multiline rows={3} label={text.notes} value={caseForm.notes} onChange={(e) => setCaseForm((v) => ({ ...v, notes: e.target.value }))} fullWidth />
            {caseMode === 'edit' && <TextField label={text.changeReason} value={caseForm.change_reason || ''} onChange={(e) => setCaseForm((v) => ({ ...v, change_reason: e.target.value }))} fullWidth />}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCaseDialog(false)}>{text.cancel}</Button>
          <Button disabled={saving} onClick={saveCase} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.deleteCaseTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">{text.deleteCaseConfirm}</Alert>
            <TextField required multiline rows={3} label={text.deleteReason} value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(false)}>{text.cancel}</Button>
          <Button disabled={saving || !deleteReason.trim()} onClick={deleteCase} variant="contained" startIcon={<DeleteOutlineIcon />} sx={{ bgcolor: COLORS.rose, fontWeight: 900, '&:hover': { bgcolor: COLORS.rose } }}>{text.deleteCase}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={examDialog} onClose={() => setExamDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.addAction}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={text.procedureType || text.examType} value={examForm.exam_type} onChange={(e) => setExamForm((v) => ({ ...v, exam_type: e.target.value }))} fullWidth>
              {actionTypeOptions.map((item) => (
                <MenuItem key={item.exam_type_key} value={item.exam_type_key}>
                  {labelOf(examTypeLabels, item.exam_type_key)}
                </MenuItem>
              ))}
            </TextField>
            {examForm.exam_type !== INSEMINATION_ACTION_KEY && !PROTOCOL_ACTION_KEYS.includes(examForm.exam_type) && (
              <TextField type="datetime-local" label={text.procedureDate || text.examDate} InputLabelProps={{ shrink: true }} value={examForm.exam_date} onChange={(e) => setExamForm((v) => ({ ...v, exam_date: e.target.value }))} fullWidth />
            )}
            {examForm.exam_type === OVULATION_TRIGGER_KEY ? (
              <Stack spacing={2}>
                <Alert severity="info">{text.triggerHint}</Alert>
                <TextField type="datetime-local" label={text.triggerDateTime} InputLabelProps={{ shrink: true }} value={triggerForm.trigger_date_time} onChange={(e) => setTriggerForm((v) => ({ ...v, trigger_date_time: e.target.value }))} fullWidth />
                <TextField select label={text.triggerMedication} value={triggerForm.trigger_medication} onChange={(e) => setTriggerForm((v) => ({ ...v, trigger_medication: e.target.value }))} fullWidth>
                  {TRIGGER_MEDICATIONS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <TextField label={text.triggerDose} value={triggerForm.trigger_dose} onChange={(e) => setTriggerForm((v) => ({ ...v, trigger_dose: e.target.value }))} fullWidth />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField type="number" label={text.follicleSize} value={triggerForm.follicle_size_at_trigger_mm} onChange={(e) => setTriggerForm((v) => ({ ...v, follicle_size_at_trigger_mm: e.target.value }))} fullWidth />
                  <TextField type="number" label={text.edemaGrade} value={triggerForm.uterine_edema_grade} onChange={(e) => setTriggerForm((v) => ({ ...v, uterine_edema_grade: e.target.value }))} fullWidth />
                </Stack>
                <TextField label={text.notes} value={triggerForm.notes} onChange={(e) => setTriggerForm((v) => ({ ...v, notes: e.target.value }))} fullWidth multiline minRows={2} />
              </Stack>
            ) : examForm.exam_type === OVULATION_CONFIRM_KEY ? (
              <Stack spacing={2}>
                <Alert severity="info">{text.confirmHint}</Alert>
                <TextField select label={text.triggerRecord} value={confirmForm.ovulation_id} onChange={(e) => setConfirmForm((v) => ({ ...v, ovulation_id: e.target.value }))} fullWidth>
                  {(caseDetails?.ovulations || []).map((row) => (
                    <MenuItem key={row.ovulation_id} value={row.ovulation_id}>
                      {`${row.trigger_medication || '-'} — ${String(row.trigger_date_time || '').replace('T', ' ').slice(0, 16)}${row.confirmed_ovulation_date_time ? ` (${text.alreadyConfirmed})` : ''}`}
                    </MenuItem>
                  ))}
                </TextField>
                {(caseDetails?.ovulations || []).length === 0 && <Alert severity="warning">{text.noTriggerYet}</Alert>}
                <TextField type="datetime-local" label={text.confirmedOvulationDateTime} InputLabelProps={{ shrink: true }} value={confirmForm.confirmed_ovulation_date_time} onChange={(e) => setConfirmForm((v) => ({ ...v, confirmed_ovulation_date_time: e.target.value }))} fullWidth />
              </Stack>
            ) : examForm.exam_type === VACCINATION_KEY ? (
              <Stack spacing={2}>
                <TextField select label={text.vaccineName} value={vaccinationForm.vaccine_name} onChange={(e) => setVaccinationForm((v) => ({ ...v, vaccine_name: e.target.value }))} fullWidth>
                  {VACCINE_NAMES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField type="number" label={text.pregnancyMonth} value={vaccinationForm.due_month_of_pregnancy} onChange={(e) => setVaccinationForm((v) => ({ ...v, due_month_of_pregnancy: e.target.value }))} fullWidth />
                  <TextField select label={text.status} value={vaccinationForm.status} onChange={(e) => setVaccinationForm((v) => ({ ...v, status: e.target.value }))} fullWidth>
                    {VACCINATION_STATUSES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField type="date" label={text.vaccineDueDate} InputLabelProps={{ shrink: true }} value={vaccinationForm.due_date} onChange={(e) => setVaccinationForm((v) => ({ ...v, due_date: e.target.value }))} fullWidth />
                  <TextField type="date" label={text.administeredDate} InputLabelProps={{ shrink: true }} value={vaccinationForm.administered_date} onChange={(e) => setVaccinationForm((v) => ({ ...v, administered_date: e.target.value }))} fullWidth />
                </Stack>
                <TextField label={text.notes} value={vaccinationForm.notes} onChange={(e) => setVaccinationForm((v) => ({ ...v, notes: e.target.value }))} fullWidth multiline minRows={2} />
              </Stack>
            ) : examForm.exam_type === FOLLOWUP_KEY ? (
              <Stack spacing={2}>
                <TextField type="datetime-local" label={text.followupDateTime} InputLabelProps={{ shrink: true }} value={followupForm.followup_date_time} onChange={(e) => setFollowupForm((v) => ({ ...v, followup_date_time: e.target.value }))} fullWidth />
                <TextField select label={text.followupType} value={followupForm.followup_type} onChange={(e) => setFollowupForm((v) => ({ ...v, followup_type: e.target.value }))} fullWidth>
                  {FOLLOWUP_TYPES.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <TextField label={text.notes} value={followupForm.notes} onChange={(e) => setFollowupForm((v) => ({ ...v, notes: e.target.value }))} fullWidth multiline minRows={2} />
                <FormControlLabel
                  control={<Checkbox checked={followupForm.next_action_required} onChange={(e) => setFollowupForm((v) => ({ ...v, next_action_required: e.target.checked }))} />}
                  label={text.nextActionRequired}
                />
                {followupForm.next_action_required && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label={text.nextActionDescription} value={followupForm.next_action_description} onChange={(e) => setFollowupForm((v) => ({ ...v, next_action_description: e.target.value }))} fullWidth />
                    <TextField type="datetime-local" label={text.nextActionDue} InputLabelProps={{ shrink: true }} value={followupForm.next_action_due_date} onChange={(e) => setFollowupForm((v) => ({ ...v, next_action_due_date: e.target.value }))} fullWidth />
                  </Stack>
                )}
              </Stack>
            ) : examForm.exam_type === INSEMINATION_ACTION_KEY ? (
              <Stack spacing={2}>
                <Alert severity="info">{text.externalStallionAllowed || text.stallionHint}</Alert>
                <Autocomplete
                  freeSolo
                  options={stallions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return [option.name_ar, option.name].filter(Boolean).join(' | ') || '';
                  }}
                  value={stallions.find((item) => Number(item.stallion_id) === Number(inseminationForm.stallion_id)) || null}
                  inputValue={inseminationForm.stallion_name}
                  onChange={(_event, option) => {
                    if (option && typeof option === 'object') {
                      setInseminationForm((v) => ({
                        ...v,
                        stallion_id: option.stallion_id || '',
                        stallion_name: option.name_ar || option.name || '',
                        semen_source: option.is_inhouse === 0 ? 'External' : 'In-house',
                        semen_batch_id: '',
                      }));
                      if (option.stallion_id) {
                        api.get('/semen/batches', { params: { stallion_id: option.stallion_id } })
                          .then((r) => setSemenBatches(Array.isArray(r.data) ? r.data : []))
                          .catch(() => setSemenBatches([]));
                      } else {
                        setSemenBatches([]);
                      }
                    } else if (typeof option === 'string') {
                      setInseminationForm((v) => ({ ...v, stallion_id: '', stallion_name: option, semen_batch_id: '' }));
                      setSemenBatches([]);
                    }
                  }}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setInseminationForm((v) => ({ ...v, stallion_id: '', stallion_name: value, semen_batch_id: '' }));
                      setSemenBatches([]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} required label={text.selectStallion || text.stallion} helperText={text.stallionHint} fullWidth />
                  )}
                />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField select label={text.semenType} value={inseminationForm.semen_type} onChange={(e) => setInseminationForm((v) => ({ ...v, semen_type: e.target.value }))} fullWidth>
                      {['Fresh', 'Cooled', 'Frozen'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField type="datetime-local" label={text.inseminationDate} InputLabelProps={{ shrink: true }} value={inseminationForm.insemination_date_time} onChange={(e) => setInseminationForm((v) => ({ ...v, insemination_date_time: e.target.value }))} fullWidth />
                  </Grid>
                </Grid>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      select required label={text.semenBatch} value={inseminationForm.semen_batch_id}
                      onChange={(e) => setInseminationForm((v) => ({ ...v, semen_batch_id: e.target.value }))}
                      error={Boolean(inseminationForm.stallion_id) && semenBatches.length === 0}
                      helperText={Boolean(inseminationForm.stallion_id) && semenBatches.length === 0 ? text.noBatchesForStallion : text.batchHint}
                      fullWidth
                    >
                      {semenBatches.map((b) => (
                        <MenuItem key={b.batch_id} value={b.batch_id} disabled={b.doses_remaining <= 0 || ['Disposed', 'Expired'].includes(b.status)}>
                          {b.batch_no} — {b.doses_remaining} {text.dosesWord} ({b.semen_type})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="number" label={text.dosesUsed} value={inseminationForm.doses_used}
                      onChange={(e) => setInseminationForm((v) => ({ ...v, doses_used: e.target.value }))}
                      inputProps={{ min: 1 }} fullWidth
                    />
                  </Grid>
                </Grid>
                <TextField select label={text.semenSource} value={inseminationForm.semen_source} onChange={(e) => setInseminationForm((v) => ({ ...v, semen_source: e.target.value }))} fullWidth>
                  {['In-house', 'External'].map((item) => <MenuItem key={item} value={item}>{text.semenSourceLabels?.[item] || item}</MenuItem>)}
                </TextField>
                {inseminationForm.semen_source === 'External' && (
                  <TextField label={text.semenProvider} value={inseminationForm.semen_provider} onChange={(e) => setInseminationForm((v) => ({ ...v, semen_provider: e.target.value }))} fullWidth />
                )}
                <TextField label={text.doseDescription} value={inseminationForm.dose_description} onChange={(e) => setInseminationForm((v) => ({ ...v, dose_description: e.target.value }))} fullWidth />
                <TextField label={text.performedBy} value={inseminationForm.performed_by} onChange={(e) => setInseminationForm((v) => ({ ...v, performed_by: e.target.value }))} fullWidth />
                <TextField multiline rows={3} label={text.notes} value={inseminationForm.notes} onChange={(e) => setInseminationForm((v) => ({ ...v, notes: e.target.value }))} fullWidth />
              </Stack>
            ) : examForm.exam_type === 'Pregnancy Check' ? (
              <Stack spacing={2}>
                <TextField select label={text.pregnancyResult} value={examForm.pregnancy_status} onChange={(e) => setExamForm((v) => ({ ...v, pregnancy_status: e.target.value }))} fullWidth>
                  {PREGNANCY_STATUSES.map((item) => (
                    <MenuItem key={item} value={item}>{labelOf(pregnancyStatusLabels, item)}</MenuItem>
                  ))}
                </TextField>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Switch checked={Boolean(examForm.fetal_heartbeat)} onChange={(e) => setExamForm((v) => ({ ...v, fetal_heartbeat: e.target.checked }))} />} label={text.fetalHeartbeat} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Switch checked={Boolean(examForm.twin_detected)} onChange={(e) => setExamForm((v) => ({ ...v, twin_detected: e.target.checked }))} />} label={text.twinDetected} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={<Switch checked={Boolean(examForm.twin_management_done)} onChange={(e) => setExamForm((v) => ({ ...v, twin_management_done: e.target.checked }))} />} label={text.twinManagementDone} />
                  </Grid>
                </Grid>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}><TextField type="number" label={text.progesterone} value={examForm.progesterone_ng_ml} onChange={(e) => setExamForm((v) => ({ ...v, progesterone_ng_ml: e.target.value }))} fullWidth /></Grid>
                  <Grid item xs={12} sm={6}><TextField label={text.vetDecision} value={examForm.vet_decision} onChange={(e) => setExamForm((v) => ({ ...v, vet_decision: e.target.value }))} fullWidth /></Grid>
                </Grid>
              </Stack>
            ) : (
              <Grid container spacing={1.5}>
                {shouldShowExamField(examForm.exam_type, 'follicle_size_mm') && (
                  <Grid item xs={12} sm={6}><TextField type="number" label={text.follicle} value={examForm.follicle_size_mm} onChange={(e) => setExamForm((v) => ({ ...v, follicle_size_mm: e.target.value }))} fullWidth /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'follicle_count') && (
                  <Grid item xs={12} sm={6}><TextField type="number" label={text.follicleCount} value={examForm.follicle_count} onChange={(e) => setExamForm((v) => ({ ...v, follicle_count: e.target.value }))} fullWidth /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'uterine_edema_grade') && (
                  <Grid item xs={12} sm={6}><TextField select label={text.edema} value={examForm.uterine_edema_grade} onChange={(e) => setExamForm((v) => ({ ...v, uterine_edema_grade: e.target.value }))} fullWidth><MenuItem value="">-</MenuItem>{['0', '1', '2', '3'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</TextField></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'corpus_luteum_present') && (
                  <Grid item xs={12} sm={6}><FormControlLabel control={<Switch checked={Boolean(examForm.corpus_luteum_present)} onChange={(e) => setExamForm((v) => ({ ...v, corpus_luteum_present: e.target.checked }))} />} label={text.corpusLuteum} /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'uterine_fluid_cm') && (
                  <Grid item xs={12} sm={6}><TextField type="number" label={text.uterineFluid} value={examForm.uterine_fluid_cm} onChange={(e) => setExamForm((v) => ({ ...v, uterine_fluid_cm: e.target.value }))} fullWidth /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'cervix_status') && (
                  <Grid item xs={12} sm={6}><TextField label={text.cervixStatus} value={examForm.cervix_status} onChange={(e) => setExamForm((v) => ({ ...v, cervix_status: e.target.value }))} fullWidth /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'progesterone_ng_ml') && (
                  <Grid item xs={12} sm={6}><TextField type="number" label={text.progesterone} value={examForm.progesterone_ng_ml} onChange={(e) => setExamForm((v) => ({ ...v, progesterone_ng_ml: e.target.value }))} fullWidth /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'culture_result') && (
                  <Grid item xs={12} sm={6}><TextField select label={text.cultureResult} value={examForm.culture_result} onChange={(e) => setExamForm((v) => ({ ...v, culture_result: e.target.value }))} fullWidth>{['Not Done', 'Negative', 'Positive', 'Pending'].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</TextField></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'cytology_result') && (
                  <Grid item xs={12} sm={6}><TextField label={text.cytologyResult} value={examForm.cytology_result} onChange={(e) => setExamForm((v) => ({ ...v, cytology_result: e.target.value }))} fullWidth /></Grid>
                )}
                {shouldShowExamField(examForm.exam_type, 'vet_decision') && (
                  <Grid item xs={12}><TextField label={text.vetDecision} value={examForm.vet_decision} onChange={(e) => setExamForm((v) => ({ ...v, vet_decision: e.target.value }))} fullWidth /></Grid>
                )}
              </Grid>
            )}
            {examForm.exam_type !== INSEMINATION_ACTION_KEY && !PROTOCOL_ACTION_KEYS.includes(examForm.exam_type) && (
              <>
                <TextField type="datetime-local" label={text.nextCheck} helperText={text.autoDateHint} InputLabelProps={{ shrink: true }} value={examForm.actual_next_check_date} onChange={(e) => setExamForm((v) => ({ ...v, actual_next_check_date: e.target.value }))} fullWidth />
                <TextField label={text.overrideReason} value={examForm.override_reason} onChange={(e) => setExamForm((v) => ({ ...v, override_reason: e.target.value }))} fullWidth />
                <TextField multiline rows={3} label={text.notes} value={examForm.notes} onChange={(e) => setExamForm((v) => ({ ...v, notes: e.target.value }))} fullWidth />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExamDialog(false)}>{text.cancel}</Button>
          <Button disabled={saving || !selectedCase || (examForm.exam_type === INSEMINATION_ACTION_KEY && !inseminationForm.stallion_name.trim())} onClick={saveExam} variant="contained" startIcon={<SaveIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={protocolDialog} onClose={() => setProtocolDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.protocol}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={text.value} value={protocolForm.parameter_value} onChange={(e) => setProtocolForm((v) => ({ ...v, parameter_value: e.target.value }))} fullWidth />
            <TextField required multiline rows={3} label={text.changeReason} value={protocolForm.change_reason} onChange={(e) => setProtocolForm((v) => ({ ...v, change_reason: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProtocolDialog(false)}>{text.cancel}</Button>
          <Button disabled={saving || !protocolForm.change_reason} onClick={saveProtocol} variant="contained" sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(scheduleDialogRow)} onClose={() => setScheduleDialogRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.scheduleAction}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
              {scheduleDialogRow ? getScheduleTitle(scheduleDialogRow, lang) : '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.muted }}>
              {scheduleDialogRow ? getScheduleNote(scheduleDialogRow, lang) : ''}
            </Typography>
            <Chip
              label={scheduleDialogRow?.status || scheduleDialogRow?.priority || '-'}
              sx={{ bgcolor: COLORS.goldSoft, alignSelf: 'flex-start', fontWeight: 900 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setScheduleDialogRow(null)}>{text.cancel}</Button>
          <Button onClick={openScheduleCase} sx={{ color: COLORS.blue, fontWeight: 900 }}>{text.openCase}</Button>
          <Button variant="contained" onClick={addExamFromSchedule} startIcon={<AddIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
            {text.addAction}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// 3.6.2 — كانت تُرجع المفتاح الخام لكلّ نوع عدا اثنين، فتظهر شارات «Insemination»
// و«Examination» و«Case» وسط نصّ عربيّ. المفردات كلّها صارت في وحدة واحدة مولَّدة من
// مصدر المسارين، وتبقى أسماء الشاشة من جدول COPY مقدَّمةً حيث عُرِّفت.
function timelineTypeLabel(type, text = {}, lang = 'en') {
  if (type === 'Scheduled' && text.scheduledItem) return text.scheduledItem;
  if (type === 'Task' && text.taskItem) return text.taskItem;
  return timelineTypeText(type, lang);
}

function timelineTypeColor(type, status) {
  if (status === 'Overdue') return COLORS.roseSoft;
  if (type === 'Scheduled') return COLORS.blueSoft;
  if (type === 'Task') return COLORS.goldSoft;
  return COLORS.cardSoft;
}

function TimelineList({ rows, empty, onDelete, deleteLabel, text, lang = 'en' }) {
  return (
    <SimpleList
      rows={rows}
      emptyText={empty}
      render={(row, index) => (
        <Stack key={`${row.type}-${row.source_id || index}`} direction="row" spacing={1.5}>
          <Box sx={{ width: 12, pt: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.status === 'Emergency' ? COLORS.rose : COLORS.gold }} />
          </Box>
          <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.card, flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" gap={1}>
              <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                {timelineTitle(lang === 'ar' ? (row.title_ar || row.title_en) : (row.title_en || row.title_ar), lang)}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip size="small" label={timelineTypeLabel(row.type, text, lang)} sx={{ bgcolor: timelineTypeColor(row.type, row.status), fontWeight: 800 }} />
                {['Scheduled', 'Task'].includes(row.type) && row.status && (
                  <Chip size="small" label={timelineStatusLabel(row.status, lang)} sx={{ bgcolor: COLORS.card, color: row.status === 'Overdue' ? COLORS.rose : COLORS.text, fontWeight: 800 }} />
                )}
                {onDelete && ['Examination', 'Pregnancy Check'].includes(row.type) && (
                  <IconButton size="small" aria-label={deleteLabel} onClick={() => onDelete(row)} sx={{ color: COLORS.rose }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            <Typography variant="caption" sx={{ color: COLORS.muted }}><DateTimeText value={row.date_time} /></Typography>
            {row.notes && <Typography variant="body2" sx={{ color: COLORS.muted, mt: 0.7 }}>{timelineNote(row.notes, lang)}</Typography>}
          </Paper>
        </Stack>
      )}
    />
  );
}

function CaseSummary({ row, text, caseStatusLabels }) {
  if (!row) return null;
  return (
    <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.cardSoft }}>
      <Grid container spacing={1.2}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>{text.status}</Typography>
          <Box sx={{ mt: 0.5 }}>
            <StatusChip value={row.case_status} label={labelOf(caseStatusLabels, row.case_status)} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>{text.confirmedOvulation}</Typography>
          <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
            {row.confirmed_ovulation_date ? <DateText value={row.confirmed_ovulation_date} /> : '-'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>{text.expectedFoaling}</Typography>
          <Typography sx={{ color: row.expected_foaling_date ? COLORS.green : COLORS.text, fontWeight: 900 }}>
            {row.expected_foaling_date ? <DateText value={row.expected_foaling_date} /> : '-'}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

function SchedulePill({ row, text, lang, dense = false, onOpen }) {
  const kind = row._schedule_kind || scheduleKind(row);
  const isTask = kind === 'task';
  const visual = scheduleVisual(row, text);
  const dateValue = getScheduleDate(row);
  const shortNote = getScheduleNote(row, lang);
  const horseTitle = getScheduleHorse(row, lang);
  const actionTitle = getScheduleAction(row, lang) || getScheduleTitle(row, lang);
  return (
    <Box
      role={row?.case_id ? 'button' : undefined}
      tabIndex={row?.case_id ? 0 : undefined}
      onClick={() => row?.case_id && onOpen?.(row)}
      onKeyDown={(event) => {
        if (row?.case_id && (event.key === 'Enter' || event.key === ' ')) onOpen?.(row);
      }}
      sx={{
        position: 'relative',
        border: `1px solid ${visual.border}`,
        bgcolor: visual.bg,
        borderRadius: 1.4,
        px: 1,
        py: dense ? 0.55 : 0.8,
        pl: lang === 'ar' ? 1 : 1.25,
        pr: lang === 'ar' ? 1.25 : 1,
        minWidth: 0,
        cursor: row?.case_id ? 'pointer' : 'default',
        transition: 'transform 140ms ease, box-shadow 140ms ease',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          insetBlock: 0,
          [lang === 'ar' ? 'right' : 'left']: 0,
          width: 4,
          bgcolor: visual.accent,
        },
        '&:hover': row?.case_id
          ? { transform: 'translateY(-1px)', boxShadow: '0 10px 18px rgba(91,75,39,0.10)' }
          : undefined,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={0.8}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: COLORS.text, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {horseTitle || actionTitle || '-'}
          </Typography>
          {horseTitle && actionTitle && (
            <Typography variant="caption" sx={{ color: visual.fg, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {actionTitle}
            </Typography>
          )}
        </Box>
        {dateValue && (
          <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800, flexShrink: 0 }}>
            {formatTime(dateValue, lang)}
          </Typography>
        )}
      </Stack>
      <Typography
        variant="caption"
        sx={{
          color: visual.key === 'overdue' ? COLORS.rose : COLORS.muted,
          display: 'block',
          mt: 0.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: visual.key === 'overdue' ? 900 : 700,
        }}
      >
        {shortNote}
      </Typography>
      {!dense && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.4 }}>
          <Chip
            size="small"
            label={visual.label || (isTask ? text.task : text.event)}
            sx={{
              height: 20,
              bgcolor: COLORS.card,
              color: visual.fg,
              fontSize: '0.68rem',
              fontWeight: 900,
            }}
          />
          {(row.status || row.priority) && (
            <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>
              {row.status || row.priority}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function WeeklySchedule({ rows, text, lang, onOpen }) {
  const locale = localeFor(lang);
  const weekStart = startOfWeek(new Date());
  const days = Array.from({ length: 7 }, (_item, index) => addDays(weekStart, index));
  const weekEnd = addDays(weekStart, 7);
  const grouped = groupScheduleByDay((rows || []).filter((row) => {
    const value = getScheduleDate(row);
    if (!value) return false;
    const itemDate = new Date(value);
    return itemDate >= weekStart && itemDate < weekEnd;
  }));

  return (
    <Stack spacing={1.2}>
      <CalendarLegend text={text} />
      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(172px, 1fr))',
            gap: 1.2,
            minWidth: 1180,
          }}
        >
          {days.map((day) => {
            const items = grouped[dateKey(day)] || [];
            const isToday = dateKey(day) === dateKey(new Date());
            return (
              <Paper
                key={dateKey(day)}
                elevation={0}
                sx={{
                  p: 1.2,
                  minHeight: 190,
                  border: `1px solid ${isToday ? COLORS.gold : COLORS.line}`,
                  borderRadius: 2,
                  bgcolor: isToday ? '#fffaf0' : COLORS.card,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                      {day.toLocaleDateString(locale, { weekday: 'short' })}
                    </Typography>
                    <Typography variant="caption" className="date-ltr" sx={{ color: COLORS.muted, fontWeight: 800 }}>
                      {formatDate(day)}
                    </Typography>
                  </Box>
                  <Chip size="small" label={items.length} sx={{ bgcolor: isToday ? COLORS.goldSoft : COLORS.cardSoft, fontWeight: 900 }} />
                </Stack>
                <Stack spacing={0.8}>
                  {items.length ? items.map((row, index) => (
                    <SchedulePill key={`${row._schedule_kind || scheduleKind(row)}-${row.event_id || row.task_id || index}`} row={row} text={text} lang={lang} onOpen={onOpen} />
                  )) : (
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>{text.noItemsForDay}</Typography>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
}

function MonthlyCalendar({ rows, month, onMonthChange, text, lang, onOpen }) {
  const locale = localeFor(lang);
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = startOfWeek(monthStart);
  const days = Array.from({ length: 42 }, (_item, index) => addDays(gridStart, index));
  const grouped = groupScheduleByDay(rows || []);
  const monthTitle = monthStart.toLocaleDateString(locale, { month: 'long' });

  const shiftMonth = (amount) => {
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + amount, 1));
  };

  return (
    <Stack spacing={1.4}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1}>
        <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
          {monthTitle} <span className="date-ltr">{monthStart.getFullYear()}</span>
        </Typography>
        <Stack direction="row" spacing={1} justifyContent={{ xs: 'space-between', sm: 'flex-end' }}>
          <Button size="small" variant="outlined" startIcon={<ChevronLeftIcon />} onClick={() => shiftMonth(-1)} sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800 }}>{text.previousMonth}</Button>
          <Button size="small" variant="outlined" onClick={() => onMonthChange(new Date())} sx={{ borderColor: COLORS.gold, color: COLORS.gold, fontWeight: 900 }}>{text.today}</Button>
          <Button size="small" variant="outlined" endIcon={<ChevronRightIcon />} onClick={() => shiftMonth(1)} sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800 }}>{text.nextMonth}</Button>
        </Stack>
      </Stack>
      <CalendarLegend text={text} />

      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Box sx={{ minWidth: 980 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: COLORS.cardSoft, border: `1px solid ${COLORS.line}`, borderBottom: 0 }}>
            {Array.from({ length: 7 }, (_item, index) => addDays(startOfWeek(new Date()), index)).map((day, index) => (
              <Typography key={index} variant="caption" sx={{ p: 1, color: COLORS.text, fontWeight: 900, textAlign: 'center' }}>
                {day.toLocaleDateString(locale, { weekday: 'short' })}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderTop: `1px solid ${COLORS.line}`, borderLeft: `1px solid ${COLORS.line}` }}>
            {days.map((day) => {
              const key = dateKey(day);
              const items = grouped[key] || [];
              const isCurrentMonth = day.getMonth() === month.getMonth();
              const isToday = key === dateKey(new Date());
              const visibleItems = items.slice(0, 3);
              return (
                <Box
                  key={key}
                  sx={{
                    minHeight: 130,
                    p: 1,
                    borderRight: `1px solid ${COLORS.line}`,
                    borderBottom: `1px solid ${COLORS.line}`,
                    bgcolor: isToday ? '#fffaf0' : isCurrentMonth ? COLORS.card : COLORS.page,
                    opacity: isCurrentMonth ? 1 : 0.58,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                    <Typography className="date-ltr" sx={{ color: isToday ? COLORS.gold : COLORS.text, fontWeight: 900 }}>
                      {String(day.getDate()).padStart(2, '0')}
                    </Typography>
                    {!!items.length && (
                      <Chip size="small" label={items.length} sx={{ height: 22, bgcolor: COLORS.goldSoft, fontWeight: 900 }} />
                    )}
                  </Stack>
                  <Stack spacing={0.65}>
                    {visibleItems.map((row, index) => (
                      <SchedulePill key={`${row.event_id || row.task_id || index}-${key}`} row={row} text={text} lang={lang} dense onOpen={onOpen} />
                    ))}
                    {items.length > visibleItems.length && (
                      <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 900 }}>
                        +{items.length - visibleItems.length}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}

function CalendarLegend({ text }) {
  return (
    <Paper elevation={0} sx={{ p: 1, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.cardSoft }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ color: COLORS.text, fontWeight: 900, mr: 0.5 }}>
          {text.calendarLegend}
        </Typography>
        {calendarLegendItems(text).map((item) => (
          <Chip
            key={item.key}
            size="small"
            label={item.label}
            sx={{
              bgcolor: item.visual.bg,
              color: item.visual.fg,
              border: `1px solid ${item.visual.border}`,
              fontWeight: 900,
            }}
          />
        ))}
      </Stack>
    </Paper>
  );
}

// 3.6.6 — نافذة عمل لا أرشيف: المستحقّ خلال ٤٨ ساعة وكلّ ما فات. وأُسقط الإسناد إلى طبيب
// بعينه — المناوب هو من ينفّذ، فالإسناد حقلٌ يُملأ ولا يُقرأ. والمطلوب من الطبيب أحد
// أمرين: أنجزها وسجّل النتيجة، أو ألغِها وقل لماذا.
function TaskList({ rows, empty, onOpen, text, lang }) {
  return <SimpleList rows={rows} emptyText={empty} render={(row) => (
    <Paper key={row.task_id} elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.card }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" gap={1}>
        <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
          {lang === 'ar' ? (row.task_title_ar || row.task_title_en) : (row.task_title_en || row.task_title_ar)}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {row.priority && row.priority !== 'Normal' && (
            <Chip size="small" label={taskPriorityLabel(row.priority, lang)} sx={{ bgcolor: row.priority === 'Critical' ? COLORS.roseSoft : COLORS.warningSoft, fontWeight: 800 }} />
          )}
          <Chip
            size="small"
            label={isOverdueTask(row) ? text.overdue : timelineStatusLabel(row.status, lang)}
            sx={{ bgcolor: isOverdueTask(row) ? COLORS.roseSoft : COLORS.goldSoft, fontWeight: 800 }}
          />
        </Stack>
      </Stack>
      <Typography variant="caption" sx={{ color: COLORS.muted }}>
        <DateTimeText value={row.due_date_time} />
        {(row.horse_name_ar || row.horse_name) ? ` · ${lang === 'ar' ? (row.horse_name_ar || row.horse_name) : (row.horse_name || row.horse_name_ar)}` : ''}
      </Typography>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button size="small" variant="contained" startIcon={<CheckCircleIcon />} onClick={() => onOpen(row)}>
          {text.taskActions}
        </Button>
      </Stack>
    </Paper>
  )} />;
}

const TASK_PRIORITIES = {
  Critical: { ar: 'حَرِج', en: 'Critical' },
  High: { ar: 'عالٍ', en: 'High' },
  Normal: { ar: 'عاديّ', en: 'Normal' },
  Low: { ar: 'منخفض', en: 'Low' },
};

function taskPriorityLabel(priority, lang) {
  const pair = TASK_PRIORITIES[priority];
  if (!pair) return priority;
  return lang === 'ar' ? pair.ar : pair.en;
}

/** فات موعدها فعلًا، لا بحسب عمود الحالة: لا شيء في النظام يرقّي Pending إلى Overdue. */
function isOverdueTask(row) {
  if (row.status === 'Overdue') return true;
  if (!row.due_date_time) return false;
  const due = new Date(row.due_date_time).getTime();
  return Number.isFinite(due) && due < Date.now();
}

function NotificationList({ rows, empty }) {
  return <SimpleList rows={rows} emptyText={empty} render={(row) => (
    <Alert key={row.notification_id} severity={row.priority === 'Critical' ? 'error' : row.priority === 'High' ? 'warning' : 'info'}>
      <Typography sx={{ fontWeight: 900 }}>{row.title_ar || row.title_en}</Typography>
      <Typography variant="caption">{row.message_ar || row.message_en || <DateTimeText value={row.due_date_time} />}</Typography>
    </Alert>
  )} />;
}

function FollowupList({ rows, empty }) {
  return <SimpleList rows={rows} emptyText={empty} render={(row) => (
    <Paper key={row.followup_id} elevation={0} sx={{ p: 1.2, border: `1px solid ${COLORS.line}`, borderRadius: 2 }}>
      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.followup_type}</Typography>
      <Typography variant="caption" sx={{ color: COLORS.muted }}><DateTimeText value={row.followup_date_time} /> · {row.followup_status}</Typography>
    </Paper>
  )} />;
}

function ProtocolChangeList({ rows, empty }) {
  return <SimpleList rows={rows} emptyText={empty} render={(row) => (
    <Paper key={row.log_id} elevation={0} sx={{ p: 1.2, border: `1px solid ${COLORS.line}`, borderRadius: 2 }}>
      <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.parameter_name_ar || row.parameter_name_en}</Typography>
      <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.old_value} → {row.new_value} · <DateTimeText value={row.changed_at} /></Typography>
    </Paper>
  )} />;
}

function TimesheetList({ rows, empty }) {
  return <SimpleList rows={rows} emptyText={empty} render={(row) => (
    <Paper key={row.timesheet_id} elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: COLORS.card }}>
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.horse_name_ar || row.horse_name || row.activity_type}</Typography>
        <Chip size="small" label={`${row.duration_minutes || 0} min`} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 800 }} />
      </Stack>
      <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.activity_type} · <DateText value={row.work_date} /> · {row.approval_status}</Typography>
    </Paper>
  )} />;
}

function TimesheetSummary({ rows, text }) {
  const summary = (rows || []).reduce(
    (acc, row) => {
      const minutes = Number(row.duration_minutes || 0);
      acc.totalMinutes += minutes;
      if (Number(row.billable || 0) === 1) acc.billableMinutes += minutes;
      acc.totalCost += Number(row.total_cost || 0);
      if (row.approval_status === 'Pending') acc.pending += 1;
      if (row.user_id) acc.users.add(row.user_id);
      return acc;
    },
    { totalMinutes: 0, billableMinutes: 0, totalCost: 0, pending: 0, users: new Set() },
  );

  const cards = [
    { label: text.timesheetTotalTime, value: `${Math.round(summary.totalMinutes / 60)} h`, color: COLORS.gold, soft: COLORS.goldSoft },
    { label: text.timesheetBillableTime, value: `${Math.round(summary.billableMinutes / 60)} h`, color: COLORS.green, soft: COLORS.greenSoft },
    { label: text.timesheetCost, value: `${summary.totalCost.toLocaleString('en-US')} SAR`, color: COLORS.blue, soft: COLORS.blueSoft },
    { label: text.timesheetPending, value: summary.pending, color: COLORS.rose, soft: COLORS.roseSoft },
  ];

  return (
    <Grid container spacing={1.5}>
      {cards.map((card) => (
        <Grid item xs={12} sm={6} md={3} key={card.label}>
          <Paper elevation={0} sx={{ p: 1.6, border: `1px solid ${COLORS.line}`, borderRadius: 2, bgcolor: card.soft }}>
            <Typography variant="caption" sx={{ color: card.color, fontWeight: 900 }}>
              {card.label}
            </Typography>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {card.value}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// 3.6.2 — أُزيلت أعمدة المبلغ/الإجماليّ/المدفوع/المتبقّي من هذا الجدول بقرار المستخدم.
// الأرقام كانت مرتبطة فعلًا بـreproduction_case_charges، لكنّها تظهر «-» في كلّ حالة بلا
// رسوم — وخيل المركز لا يُفوتَر أصلًا — فصارت أربعة أعمدة فارغة تزاحم أعمدة المتابعة
// الطبّيّة. الأرقام المالية موضعها صفحات المحاسبة حيث تكتمل بالفاتورة والمدفوع.
function SeasonFollowupTable({ title, rows, text }) {
  const columns = [
    ['#', (row, index) => index + 1, 54],
    [text.caseSerial, (row) => row.case_id ? `#${row.case_id}` : '-', 100],
    [text.mareRoom, (row) => row.mare_room || row.rfid_chip_number || '-', 112],
    [text.mare, (row) => row.mare_name_ar || row.mare_name || '-', 220],
    [text.stallion, (row) => row.stallion_name || '-', 180],
    [text.ovulationShort, (row) => <DateText value={row.ovulation_date} />, 104],
    [text.heartbeatShort, (row) => formatSeasonValue(row.heartbeat_status), 104],
    [text.stopP4, (row) => <DateText value={row.stop_p4_date} />, 104],
    [text.vaccine5m, (row) => <DateText value={row.vaccine_5m_date} />, 104],
    [text.vaccine7m, (row) => <DateText value={row.vaccine_7m_date} />, 104],
    [text.vaccine9m, (row) => <DateText value={row.vaccine_9m_date} />, 104],
    [text.deworm, (row) => <DateText value={row.deworm_date} />, 112],
    [text.dueDate, (row) => <DateText value={row.due_date} />, 112],
    [text.obs, (row) => row.observation || '-', 240],
  ];

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${COLORS.line}`, borderRadius: 2, overflow: 'hidden', bgcolor: COLORS.card }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ p: 1.5, bgcolor: COLORS.cardSoft }}>
        <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{title}</Typography>
        <Chip size="small" label={`${rows.length} ${text.cases}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: 1450, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: COLORS.goldSoft }}>
              {columns.map(([label, _render, width]) => (
                <Box
                  key={label}
                  component="th"
                  sx={{
                    width,
                    px: 1.25,
                    py: 1.15,
                    color: COLORS.text,
                    fontSize: 13,
                    fontWeight: 900,
                    textAlign: 'start',
                    borderBottom: `1px solid ${COLORS.line}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.length ? rows.map((row, index) => (
              <Box key={`${title}-${row.case_id || index}`} component="tr" sx={{ '&:nth-of-type(even)': { bgcolor: '#fffaf0' } }}>
                {columns.map(([label, render]) => (
                  <Box
                    key={`${row.case_id || index}-${label}`}
                    component="td"
                    sx={{
                      px: 1.25,
                      py: 1.2,
                      color: COLORS.text,
                      fontSize: 13,
                      borderBottom: `1px solid ${COLORS.line}`,
                      verticalAlign: 'top',
                      lineHeight: 1.5,
                    }}
                  >
                    {render(row, index)}
                  </Box>
                ))}
              </Box>
            )) : (
              <Box component="tr">
                <Box component="td" colSpan={columns.length} sx={{ px: 1.5, py: 2, color: COLORS.muted }}>
                  {text.noData}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

function formatSeasonValue(value) {
  if (!value) return '-';
  const asDate = new Date(value);
  if (!Number.isNaN(asDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(String(value))) return formatDate(value);
  return String(value);
}

function Reports({ reports, text }) {
  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={6}>
        <Section title={text.pregnancyRate || 'Pregnancy rate'}>
          <SimpleList rows={reports.pregnancyRate} emptyText={text.noData} render={(row) => (
            <Paper key={row.stallion} elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}`, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>{row.stallion}</Typography>
                <Chip label={`${row.pregnancy_rate || 0}%`} sx={{ bgcolor: COLORS.greenSoft, fontWeight: 900 }} />
              </Stack>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.pregnant_cases || 0}/{row.bred_cases || 0}</Typography>
            </Paper>
          )} />
        </Section>
      </Grid>
      <Grid item xs={12} md={6}>
        <Section title={text.embryoRecovery || 'Embryo recovery'}>
          <Stack spacing={1}>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>Flushes: {reports.embryoRecovery?.flushes || 0}</Typography>
            <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>Embryos: {reports.embryoRecovery?.embryos || 0}</Typography>
            <Divider />
            <Chip label={`${reports.embryoRecovery?.recovery_rate || 0}%`} sx={{ bgcolor: COLORS.blueSoft, fontWeight: 900, alignSelf: 'flex-start' }} />
          </Stack>
        </Section>
      </Grid>
    </Grid>
  );
}
